/* eslint-disable no-use-before-define */
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Plus, Edit, Play, Pause, Workflow, Mail, ListTree, Activity, Clock, ChevronRight, AlertTriangle,
    Trophy, Users, Target, Zap, ShieldCheck, BarChart3, MessageSquare, Eye, ArrowRight, CheckCircle, ChevronLeft,
    Trash2, RefreshCw, RotateCcw, FileText
} from 'lucide-react';
import { SnackbarContext } from '../App';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { api } from '../api/api';
import SequenceGraphEditor from './SequenceGraphEditor';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import DataTable from './ui/DataTable';
import StatusBadge from './ui/StatusBadge';
import EmptyState from './ui/EmptyState';
import StatCard from './ui/StatCard';
import SequenceEnrollModal from './SequenceEnrollModal';
import { UserPlus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { cn } from '../lib/utils';
import { formatDate, formatDateTime, formatNumber, normalizeApiError, formatRelativeDistance } from '../lib/i18n';
import { TimezoneSelector } from './ui/TimezoneSelector';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from "./ui/sheet";
import TemplateStyleEmailEditorShell from './TemplateStyleEmailEditorShell';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

const SEQUENCE_EDITOR_VARIABLES = ['firstName', 'lastName', 'company', 'title', 'email'];



// Helper to extract a linear-like timeline from a graph for display purposes
const getGraphSequenceTimeline = (graph) => {
    if (!graph || !graph.nodes || !graph.edges) return [];

    const timeline = [];
    const visited = new Set();

    // Find START node
    let currentNode = graph.nodes.find(n => n.data?.type === 'START' || n.type === 'start');
    if (!currentNode) return [];

    let currentDelay = 0;

    // Sequential traversal following the primary path
    while (currentNode && !visited.has(currentNode.id)) {
        visited.add(currentNode.id);
        const type = (currentNode.data?.type || currentNode.type || '').toUpperCase();
        const nodeId = currentNode.id;
        const nodeData = currentNode.data;

        if (type === 'SEND_EMAIL') {
            timeline.push({
                type: 'EMAIL',
                label: nodeData?.stepLabel || 'Email Step',
                delayDays: currentDelay,
                subject: nodeData?.subjectTemplate || '(No subject)',
                nodeId: nodeId
            });
            currentDelay = 0; // Reset delay after email
        } else if (type === 'WAIT') {
            currentDelay += (parseInt(nodeData?.delayDays) || 0);
        }

        // Find next node
        // For conditions (IF_NO_REPLY), we follow the 'true' handle which represents the 'no reply' continuation (reminder)
        const outgoingEdges = graph.edges.filter(e => e.source === nodeId);
        let nextEdge = null;

        if (['IF_NO_REPLY', 'IF_REPLIED', 'IF_BOUNCED'].includes(type)) {
            // For these conditions, the 'true' handle is usually the one leading to the next follow-up
            nextEdge = outgoingEdges.find(e => e.sourceHandle === 'true') || outgoingEdges[0];
        } else {
            nextEdge = outgoingEdges[0];
        }

        if (nextEdge) {
            currentNode = graph.nodes.find(n => n.id === nextEdge.target);
        } else {
            currentNode = null;
        }
    }

    return timeline;
};

const getEnrollmentSourceLabel = (source, t) => {
    const labels = {
        MANUAL_CONTACT_SELECTION: t('sequences.sources.manualSelection'),
        GROUP_SELECTION: t('sequences.sources.groupEnrollment'),
        LIST_SELECTION: t('sequences.sources.listEnrollment'),
        COMPANY_CONTACT_SELECTION: t('sequences.sources.companyEnrollment'),
        IMPORT: t('sequences.sources.import'),
        AUTOMATION: t('sequences.sources.automation')
    };
    return labels[source] || source?.replace(/_/g, ' ') || t('common.unknown');
};

const getAssignmentTriggerLabel = (assignment, t) => {
    if (assignment.status === 'PAUSED') return t('sequences.assignment.manualPauseActive');
    if (assignment.status === 'REPLIED') return t('sequences.assignment.replyDetected');
    if (assignment.status === 'BOUNCED') return t('sequences.assignment.bounceDetected');
    if (assignment.status === 'FAILED') return t('sequences.assignment.executionFailed');
    if ((assignment.status === 'ACTIVE' || assignment.status === 'PENDING') && assignment.nextStepScheduledAt) {
        return t('sequences.assignment.scheduledWait');
    }
    if (assignment.latestActionType) {
        return assignment.latestActionType.replace(/_/g, ' ');
    }
    return t('sequences.assignment.awaitingEvaluation');
};

const getAssignmentNextAction = (assignment, t, language) => {
    if (assignment.status === 'PAUSED') return assignment.pauseReason || t('sequences.assignment.resumeRequired');
    if (assignment.status === 'FAILED') return t('sequences.assignment.retryRequired');
    if (assignment.status === 'REPLIED' || assignment.status === 'BOUNCED') return t('sequences.assignment.sequenceClosed');
    if (assignment.nextStepScheduledAt) {
        return t('sequences.assignment.nextActionAt', {
            date: formatDateTime(assignment.nextStepScheduledAt, {}, language),
        });
    }
    return t('sequences.assignment.workerNextAction');
};

const getLinearStepDetails = (step, idx, t) => {
    const waitLabel = idx === 0
        ? t('sequences.linear.immediateEntry')
        : t('sequences.linear.waitBeforeStep', { count: step.delayDays || 0 });

    return {
        title: step.subjectTemplate || t('sequences.linear.stepTitle', { number: idx + 1 }),
        trigger: waitLabel,
        description: step.bodyTemplate
            ? step.bodyTemplate.replace(/\s+/g, ' ').trim().slice(0, 140)
            : t('sequences.linear.noBody')
    };
};

const getSequenceSenderDisplay = (sequence, mailAccounts, senderPools, t) => {
    if (sequence.sendingSourceType === 'POOL') {
        const poolObj = sequence.senderPoolId && typeof sequence.senderPoolId === 'object'
            ? sequence.senderPoolId
            : senderPools.find(p => p._id === sequence.senderPoolId);
        if (!poolObj) return t('sequences.sender.poolMissing');
        const suffix = poolObj.isActive === false ? ` (${t('sequences.sender.inactive')})` : '';
        return `${poolObj.name || t('sequences.sender.linkedPool')}${suffix}`;
    }

    const accountObj = sequence.defaultSenderAccountId && typeof sequence.defaultSenderAccountId === 'object'
        ? sequence.defaultSenderAccountId
        : mailAccounts.find(a => a._id === sequence.defaultSenderAccountId);
    if (!accountObj) return t('sequences.sender.accountMissing');
    const label = accountObj.fromName || accountObj.from || accountObj.user || t('sequences.sender.defaultAccount');
    return `${label}${accountObj.active === false ? ` (${t('sequences.sender.inactive')})` : ''}`;
};



// Advanced Selection Popover Component
const SmartSelectionPopover = ({
    totalRecords,
    onApply,
    currentPageCount,
    onSelectAll,
    onSelectPage,
    isAllSelectedGlobal,
}) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState('PAGE'); // 'NUMBER', 'PAGE', 'ALL'
    const [targetCount, setTargetCount] = useState(25);
    const [maxPerCompany, setMaxPerCompany] = useState(1);
    const [useMaxPerCompany, setUseMaxPerCompany] = useState(false);

    return (
        <PopoverContent className="w-80 p-5 bg-card border-border shadow-2xl rounded-3xl animate-in zoom-in-95 duration-200">
            <div className="space-y-6">
                <div className="flex items-center gap-3 group cursor-pointer" onClick={() => setMode('NUMBER')}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${mode === 'NUMBER' ? 'border-primary bg-primary' : 'border-muted-foreground/30'}`}>
                        {mode === 'NUMBER' && <div className="w-2 h-2 rounded-full bg-white animate-in fade-in zoom-in" />}
                    </div>
                    <Label className="text-sm font-black tracking-tight cursor-pointer">{t('sequences.selection.selectNumber')}</Label>
                </div>

                {mode === 'NUMBER' && (
                    <div className="ml-8 space-y-4 animate-in slide-in-from-top-2 duration-300">
                        <Input
                            type="number"
                            value={targetCount}
                            onChange={(e) => setTargetCount(parseInt(e.target.value) || 0)}
                            className="h-11 rounded-xl bg-muted/20 border-border/60 font-bold focus-visible:ring-primary/20"
                        />
                        <div className="flex items-center gap-3">
                            <Checkbox
                                id="max-per-co"
                                checked={useMaxPerCompany}
                                onCheckedChange={setUseMaxPerCompany}
                                className="rounded-md border-border/60 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <Label htmlFor="max-per-co" className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest cursor-pointer">{t('sequences.selection.maxPerCompany')}</Label>
                            {useMaxPerCompany && (
                                <Input
                                    type="number"
                                    value={maxPerCompany}
                                    onChange={(e) => setMaxPerCompany(parseInt(e.target.value) || 1)}
                                    className="w-16 h-8 text-xs rounded-lg font-bold bg-muted/20"
                                />
                            )}
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-3 group cursor-pointer" onClick={() => setMode('PAGE')}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${mode === 'PAGE' ? 'border-primary bg-primary' : 'border-muted-foreground/30'}`}>
                        {mode === 'PAGE' && <div className="w-2 h-2 rounded-full bg-white animate-in fade-in zoom-in" />}
                    </div>
                    <div className="flex-1 flex justify-between items-center cursor-pointer">
                        <Label className="text-sm font-black tracking-tight cursor-pointer">{t('sequences.selection.selectPage')}</Label>
                        <span className="text-[10px] font-bold bg-muted px-2 py-0.5 rounded-md text-muted-foreground">{formatNumber(currentPageCount)}</span>
                    </div>
                </div>

                <div className="flex items-center gap-3 group cursor-pointer" onClick={() => setMode('ALL')}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${mode === 'ALL' ? 'border-primary bg-primary' : 'border-muted-foreground/30'}`}>
                        {mode === 'ALL' && <div className="w-2 h-2 rounded-full bg-white animate-in fade-in zoom-in" />}
                    </div>
                    <div className="flex-1 flex justify-between items-center cursor-pointer">
                        <Label className="text-sm font-black tracking-tight cursor-pointer">{t('sequences.selection.selectAll')}</Label>
                        <span className="text-[10px] font-bold bg-muted px-2 py-0.5 rounded-md text-muted-foreground">{formatNumber(totalRecords)}</span>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border/40">
                    <Button variant="ghost" size="sm" className="h-10 px-5 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-mutedTransition flex-1">
                        {t('actions.cancel')}
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => {
                            if (mode === 'ALL') onSelectAll();
                            else if (mode === 'PAGE') onSelectPage();
                            else onApply({ targetCount, maxPerCompany: useMaxPerCompany ? maxPerCompany : null });
                        }}
                        className="h-10 px-8 text-[11px] font-black uppercase tracking-widest bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 rounded-xl flex-1 border-none"
                    >
                        {t('sequences.selection.apply')}
                    </Button>
                </div>
            </div>
        </PopoverContent>
    );
};


export default function Sequences() {
    const showSnackbar = useContext(SnackbarContext);
    const { t, i18n } = useTranslation();
    const { '*': splat } = useParams();
    const navigate = useNavigate();
    const notifyError = (error, fallbackKey = 'errors.generic') =>
        showSnackbar(normalizeApiError(error, t, fallbackKey), 'error');

    const [sequences, setSequences] = useState([]);
    const [loading, setLoading] = useState(true);

    // UI state
    const [selectedSequence, setSelectedSequence] = useState(null);
    const [viewMode, setViewMode] = useState('LIST'); // LIST, DETAIL, EDIT_STEPS, CREATE
    const [assignments, setAssignments] = useState([]);
    const [assignmentsPagination, setAssignmentsPagination] = useState({ page: 1, limit: 10, totalRecords: 0, totalPages: 1 });
    const [selectedAssignmentIds, setSelectedAssignmentIds] = useState([]);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [assignmentSearch, setAssignmentSearch] = useState('');
    const [activeHistory, setActiveHistory] = useState(null);
    const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
    const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
    const [sequenceAnalytics, setSequenceAnalytics] = useState(null);

    // Config Edit State
    const [isEditingConfig, setIsEditingConfig] = useState(false);
    const [configForm, setConfigForm] = useState({ sendingSourceType: 'ACCOUNT', defaultSenderAccountId: '', senderPoolId: '' });
    const [isSavingConfig, setIsSavingConfig] = useState(false);

    // Form states
    const [newSeqName, setNewSeqName] = useState('');
    const [newSeqFormat, setNewSeqFormat] = useState('GRAPH');
    const [newSeqSenderAccountId, setNewSeqSenderAccountId] = useState('');
    const [mailAccounts, setMailAccounts] = useState([]);
    const [editingSteps, setEditingSteps] = useState([]);
    const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
    const [hasSendingAccounts, setHasSendingAccounts] = useState(true);
    const [senderPools, setSenderPools] = useState([]);
    const [newSeqSourceType, setNewSeqSourceType] = useState('ACCOUNT'); // ACCOUNT, POOL
    const [newSeqPoolId, setNewSeqPoolId] = useState('');
    const [isPoolBuilderOpen, setIsPoolBuilderOpen] = useState(false);
    const [poolBuilderContext, setPoolBuilderContext] = useState('CREATE');
    const [newPoolName, setNewPoolName] = useState('');
    const [newPoolAccountIds, setNewPoolAccountIds] = useState([]);
    const [newPoolRotationStrategy, setNewPoolRotationStrategy] = useState('ROUND_ROBIN');
    const [isSavingPool, setIsSavingPool] = useState(false);
    const [isUploadingStepImage, setIsUploadingStepImage] = useState(false);
    const [stepPreviews, setStepPreviews] = useState({});
    const [isStepEditorOpen, setIsStepEditorOpen] = useState(false);
    const [activeStepIndex, setActiveStepIndex] = useState(null);
    const [activeStepDraft, setActiveStepDraft] = useState(null);
    const [activeStepPreview, setActiveStepPreview] = useState(null);
    const [isActiveStepPreviewLoading, setIsActiveStepPreviewLoading] = useState(false);
    const [activeStepIsNew, setActiveStepIsNew] = useState(false);
    const stepPreviewTimeoutRef = useRef(null);

    // Scheduling State
    const [startMode, setStartMode] = useState('IMMEDIATE');
    const [scheduledAt, setScheduledAt] = useState('');
    const [endAt, setEndAt] = useState('');
    const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
    const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
    const [sendingWindowStart, setSendingWindowStart] = useState('09:00');
    const [sendingWindowEnd, setSendingWindowEnd] = useState('17:30');
    const [minDelaySec, setMinDelaySec] = useState(0);
    const [maxDelaySec, setMaxDelaySec] = useState(0);
    const [perDayLimit, setPerDayLimit] = useState(0);
    const [dispatchSortPolicy, setDispatchSortPolicy] = useState('COMPANY_SEQUENTIAL');

    // Confirmation Dialog State
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        confirmText: t('actions.save'),
        cancelText: t('actions.cancel'),
        variant: 'default'
    });

    const openConfirm = ({ title, message, onConfirm, confirmText = t('actions.save'), cancelText = t('actions.cancel'), variant = 'default' }) => {
        setConfirmDialog({
            isOpen: true,
            title,
            message,
            onConfirm: () => {
                onConfirm();
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
            },
            confirmText,
            cancelText,
            variant
        });
    };

    // Note: fetchSequences and fetchSequenceDetail are now managed in the initial block to prevent hoisting issues.

    const fetchSequences = useCallback(async () => {
        try {
            setLoading(true);
            const [seqRes, accRes, poolRes] = await Promise.all([
                api.get('/api/sequences'),
                api.get('/api/mail-accounts').catch(() => ({ data: [] })),
                api.get('/api/sender-pools').catch(() => ({ data: [] }))
            ]);
            setSequences(seqRes.data);
            const accounts = accRes.data.accounts || accRes.data.data || (Array.isArray(accRes.data) ? accRes.data : []);
            const activeAccounts = accounts.filter(a => a.active !== false);
            setMailAccounts(activeAccounts);
            setHasSendingAccounts(activeAccounts.length > 0);

            const pools = poolRes.data.pools || poolRes.data.data || (Array.isArray(poolRes.data) ? poolRes.data : []);
            setSenderPools(pools);

            if (activeAccounts.length > 0 && !newSeqSenderAccountId) {
                setNewSeqSenderAccountId(activeAccounts[0]._id);
            }
            if (pools.length > 0 && !newSeqPoolId) {
                setNewSeqPoolId(pools[0]._id);
            }
        } catch (err) {
            notifyError(err, 'sequences.messages.fetchFailed');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSequenceDetail = useCallback(async (id) => {
        try {
            const res = await api.get(`/api/sequences/${id}`);
            const data = res.data;
            setSelectedSequence(data);
            setEditingSteps(data.steps || []);

            // Sync config form
            setConfigForm({
                sendingSourceType: data.sendingSourceType || 'ACCOUNT',
                defaultSenderAccountId: data.defaultSenderAccountId?._id || data.defaultSenderAccountId || '',
                senderPoolId: data.senderPoolId?._id || data.senderPoolId || '',
                timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
                workingDays: data.workingDays || [1, 2, 3, 4, 5],
                sendingWindowStart: data.sendingWindowStart || '09:00',
                sendingWindowEnd: data.sendingWindowEnd || '17:30'
            });

            // Update individual states too for modal editing
            setStartMode(data.startMode || 'IMMEDIATE');
            setScheduledAt(data.scheduledAt ? new Date(data.scheduledAt).toISOString().slice(0, 16) : '');
            setEndAt(data.endAt ? new Date(data.endAt).toISOString().slice(0, 16) : '');
            setTimezone(data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
            setWorkingDays(data.workingDays || [1, 2, 3, 4, 5]);
            setSendingWindowEnd(data.sendingWindowEnd || '17:30');
            setMinDelaySec(data.minDelaySec || 0);
            setMaxDelaySec(data.maxDelaySec || 0);
            setPerDayLimit(data.perDayLimit || 0);
            setDispatchSortPolicy(data.dispatchSortPolicy || 'COMPANY_SEQUENTIAL');

            // Sync background data
            fetchAssignments(id, 1);
            fetchAnalytics(id);
        } catch (err) {
            notifyError(err, 'sequences.messages.fetchDetailFailed');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const generateStepPreview = useCallback(async (step) => {
        try {
            const res = await api.post('/api/templates/preview', {
                subject: step?.subjectTemplate || '',
                bodyHtml: step?.bodyTemplate || '',
                attachments: step?.attachments || []
            });
            return res.data;
        } catch (err) {
            return {
                resolvedSubject: step?.subjectTemplate || '',
                resolvedBodyHtml: step?.bodyTemplate || '',
                attachments: step?.attachments || [],
                _previewFailed: true,
            };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshStepPreviews = useCallback(async (steps) => {
        if (!steps?.length) {
            setStepPreviews({});
            return;
        }

        const previewEntries = await Promise.all(
            steps.map(async (step, idx) => [idx, await generateStepPreview(step)])
        );

        setStepPreviews(Object.fromEntries(previewEntries));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generateStepPreview]);
    // Sync URL with ViewMode
// eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!splat) {
            setViewMode('LIST');
            setSelectedSequence(null);
        } else if (splat === 'create') {
            setViewMode('CREATE');
            setSelectedSequence(null);

            // Initialize creation state
            setNewSeqName('');
            setNewSeqFormat('GRAPH');
            setNewSeqSourceType('ACCOUNT');
            if (mailAccounts.length > 0) setNewSeqSenderAccountId(mailAccounts[0]._id);
            if (senderPools.length > 0) setNewSeqPoolId(senderPools[0]._id);
            setStartMode('IMMEDIATE');
            setScheduledAt('');
            setEndAt('');
            setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
            setWorkingDays([1, 2, 3, 4, 5]);
            setSendingWindowStart('09:00');
            setSendingWindowEnd('17:30');
            setMinDelaySec(0);
            setMaxDelaySec(0);
            setPerDayLimit(0);
        } else {
            const parts = splat.split('/');
            const id = parts[0];
            const subAction = parts[1];

            if (id) {
                if (subAction === 'edit') {
                    setViewMode('EDIT_STEPS');
                } else {
                    setViewMode('DETAIL');
                }

                // Fetch detail if not already loaded or if different id
                // eslint-disable-next-line no-use-before-define
                if (!selectedSequence || selectedSequence._id !== id) {
                    fetchSequenceDetail(id);
                }
            }
        }
/* eslint-disable-next-line no-use-before-define */
    }, [splat, fetchSequenceDetail, mailAccounts, selectedSequence, senderPools]);

    useEffect(() => {
        fetchSequences();
    }, [fetchSequences]);

    useEffect(() => {
        if (viewMode !== 'EDIT_STEPS' || selectedSequence?.isGraph) return;
        refreshStepPreviews(editingSteps);
    }, [editingSteps, selectedSequence?.isGraph, viewMode, refreshStepPreviews]);

// eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!isStepEditorOpen || !activeStepDraft) return;

        if (stepPreviewTimeoutRef.current) {
            clearTimeout(stepPreviewTimeoutRef.current);
        }

        setIsActiveStepPreviewLoading(true);
        stepPreviewTimeoutRef.current = setTimeout(async () => {
            const preview = await generateStepPreview(activeStepDraft);
            setActiveStepPreview(preview);
            setIsActiveStepPreviewLoading(false);
        }, 500);

        return () => {
            if (stepPreviewTimeoutRef.current) clearTimeout(stepPreviewTimeoutRef.current);
        };
    }, [activeStepDraft, isStepEditorOpen, generateStepPreview]);

// eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (viewMode !== 'EDIT_STEPS') {
            closeStepEditor();
        }
    }, [viewMode]);

    // Note: fetchSequences and fetchSequenceDetail are now managed in the initial block to prevent hoisting issues.

    const openPoolBuilder = (context = 'CREATE') => {
        setPoolBuilderContext(context);
        setNewPoolName(
            context === 'CONFIG' && selectedSequence
                ? `${selectedSequence.name} ${t('sequences.sender.poolSuffix')}`
                : `${newSeqName || t('sidebar.sequences')} ${t('sequences.sender.poolSuffix')}`,
        );
        setNewPoolAccountIds([]);
        setNewPoolRotationStrategy('ROUND_ROBIN');
        setIsPoolBuilderOpen(true);
    };

    const togglePoolAccount = (accountId) => {
        setNewPoolAccountIds(prev => (
            prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]
        ));
    };

    const handleCreateSenderPoolInline = async () => {
        if (!newPoolName.trim()) {
            showSnackbar(t('sequences.messages.poolNameRequired'), 'error');
            return;
        }
        if (newPoolAccountIds.length === 0) {
            showSnackbar(t('sequences.messages.mailAccountRequired'), 'error');
            return;
        }

        try {
            setIsSavingPool(true);
            const res = await api.post('/api/sender-pools', {
                name: newPoolName.trim(),
                mailAccountIds: newPoolAccountIds,
                rotationStrategy: newPoolRotationStrategy,
                isActive: true
            });

            const createdPool = res.data?.data;
            if (!createdPool?._id) {
                throw new Error(t('sequences.messages.senderPoolCreationFailed'));
            }

            const nextPools = [createdPool, ...senderPools];
            setSenderPools(nextPools);

            if (poolBuilderContext === 'CONFIG') {
                setConfigForm(prev => ({
                    ...prev,
                    sendingSourceType: 'POOL',
                    senderPoolId: createdPool._id,
                    defaultSenderAccountId: ''
                }));
            } else {
                setNewSeqSourceType('POOL');
                setNewSeqPoolId(createdPool._id);
                setNewSeqSenderAccountId('');
            }

            setIsPoolBuilderOpen(false);
            showSnackbar(t('sequences.messages.senderPoolCreated'), 'success');
        } catch (err) {
            notifyError(err, 'sequences.messages.senderPoolCreateFailed');
        } finally {
            setIsSavingPool(false);
        }
    };

    const fetchAssignments = async (sequenceId, page = 1, status = statusFilter) => {
        try {
            const limit = assignmentsPagination.limit || 10;
            const params = {
                page,
                limit,
                status: status === 'ALL' ? undefined : status
            };
            if (assignmentSearch.trim()) {
                params.search = assignmentSearch.trim();
            }
            const res = await api.get(`/api/sequences/${sequenceId}/assignments`, { params });
            setAssignments(res.data.data || []);
            setAssignmentsPagination(res.data.pagination);
            setSelectedAssignmentIds([]);
        } catch (err) {
            console.error('Failed to fetch assignments', err);
        }
    };

    const fetchAnalytics = async (id) => {
        try {
            const res = await api.get(`/api/sequences/${id}/analytics`);
            setSequenceAnalytics(res.data);
        } catch (err) {
            console.error("Analytics load failed", err);
        }
    };

    const fetchAssignmentHistory = async (assignmentId) => {
        try {
            const res = await api.get(`/api/sequences/${selectedSequence._id}/assignments/${assignmentId}/history`);
            setActiveHistory(res.data?.data || null);
            setIsHistoryDrawerOpen(true);
        } catch (err) {
            notifyError(err, 'sequences.messages.historyLoadFailed');
        }
    };

    const handleBulkAction = async (action) => {
        if (!isAllSelected && !selectedAssignmentIds.length) return;

        const count = isAllSelected ? assignmentsPagination.totalRecords : selectedAssignmentIds.length;
        const confirmMsg = action === 'REMOVE'
            ? t('sequences.confirm.removeProspects', { count })
            : t('sequences.confirm.applyBulkAction', { action, count });

        openConfirm({
            title: action === 'REMOVE' ? t('sequences.confirm.removeTitle') : t('sequences.confirm.actionTitle'),
            message: confirmMsg,
            confirmText: action === 'REMOVE' ? t('sequences.confirm.yesRemove') : t('sequences.confirm.confirm'),
            variant: action === 'REMOVE' ? 'destructive' : 'default',
            onConfirm: async () => {
                try {
                    setIsBulkActionLoading(true);
                    await api.post(`/api/sequences/${selectedSequence._id}/assignments/bulk-action`, {
                        assignmentIds: selectedAssignmentIds,
                        isAllSelected,
                        filters: { status: statusFilter },
                        action
                    });
                    showSnackbar(t('sequences.messages.bulkCompleted', { action }), 'success');
                    setSelectedAssignmentIds([]);
                    setIsAllSelected(false);
                    fetchAssignments(selectedSequence._id, assignmentsPagination.page);
                } catch (err) {
                    notifyError(err, 'sequences.messages.bulkFailed');
                } finally {
                    setIsBulkActionLoading(false);
                }
            }
        });
    };

    const retryAssignment = async (assignmentId) => {
        try {
            await api.post(`/api/sequences/${selectedSequence._id}/assignments/${assignmentId}/retry`);
            showSnackbar(t('sequences.messages.identityReset'), 'success');
            fetchAssignments(selectedSequence._id, assignmentsPagination.page);
        } catch (err) {
            notifyError(err, 'sequences.messages.retryFailed');
        }
    };

    const openCreateModal = () => {
        navigate('/sequences/create');
    };

    const handleCreateSequence = async () => {
        if (!newSeqName) return;
        try {
            const res = await api.post('/api/sequences', {
                name: newSeqName,
                steps: [],
                isGraph: newSeqFormat === 'GRAPH',
                sendingSourceType: newSeqSourceType,
                defaultSenderAccountId: (newSeqSourceType === 'ACCOUNT' && newSeqSenderAccountId && newSeqSenderAccountId !== 'none') ? newSeqSenderAccountId : undefined,
                senderPoolId: (newSeqSourceType === 'POOL' && newSeqPoolId && newSeqPoolId !== 'none') ? newSeqPoolId : undefined,
                status: startMode === 'SCHEDULED' ? 'SCHEDULED' : 'DRAFT',
                startMode,
                scheduledAt: startMode === 'SCHEDULED' ? scheduledAt : undefined,
                endAt: endAt || undefined,
                timezone,
                workingDays,
                sendingWindowStart,
                sendingWindowEnd,
                minDelaySec: minDelaySec || undefined,
                maxDelaySec: maxDelaySec || undefined,
                perDayLimit: perDayLimit || undefined,
                dispatchSortPolicy: dispatchSortPolicy || 'COMPANY_SEQUENTIAL'
            });
            showSnackbar(
                startMode === 'SCHEDULED' ? t('sequences.messages.createdScheduled') : t('sequences.messages.created'),
                'success',
            );
            setNewSeqName('');
            setStartMode('IMMEDIATE');
            setScheduledAt('');
            setScheduledAt('');
            setEndAt('');
            // Reset scheduling defaults
            setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
            setWorkingDays([1, 2, 3, 4, 5]);
            setSendingWindowStart('09:00');
            setSendingWindowEnd('17:30');
            setMinDelaySec(0);
            setMaxDelaySec(0);
            setPerDayLimit(0);

            if (res.data?._id) {
                navigate(`/sequences/${res.data._id}`);
            } else {
                navigate('/sequences');
                fetchSequences();
            }
        } catch (err) {
            notifyError(err, 'sequences.messages.creationFailed');
        }
    };

    const handleStatusChange = async (id, status) => {
        try {
            await api.put(`/api/sequences/${id}/status`, { status });
            showSnackbar(t('sequences.messages.statusChanged', { status }), 'success');
            if (viewMode === 'LIST') fetchSequences();
            if (viewMode === 'DETAIL') {
                const updated = { ...selectedSequence, status };
                setSelectedSequence(updated);
            }
        } catch (err) {
            notifyError(err, 'sequences.messages.statusUpdateFailed');
        }
    };

    const saveEditingSteps = async () => {
        try {
            await api.put(`/api/sequences/${selectedSequence._id}`, { steps: editingSteps });
            showSnackbar(t('sequences.messages.stepsSaved'), 'success');
            navigate(`/sequences/${selectedSequence._id}`);
        } catch (err) {
            notifyError(err, 'sequences.messages.saveFailed');
        }
    };

    const handleSaveConfig = async () => {
        try {
            if (configForm.sendingSourceType === 'POOL' && !configForm.senderPoolId) {
                showSnackbar(t('sequences.messages.selectPoolBeforeSaving'), 'error');
                return;
            }
            setIsSavingConfig(true);
            const res = await api.put(`/api/sequences/${selectedSequence._id}`, {
                sendingSourceType: configForm.sendingSourceType,
                defaultSenderAccountId: configForm.sendingSourceType === 'ACCOUNT' ? (configForm.defaultSenderAccountId || null) : null,
                senderPoolId: configForm.sendingSourceType === 'POOL' ? (configForm.senderPoolId || null) : null,
                startMode,
                scheduledAt: startMode === 'SCHEDULED' ? scheduledAt : undefined,
                endAt: endAt || undefined,
                timezone,
                workingDays,
                sendingWindowStart,
                sendingWindowEnd,
                minDelaySec: minDelaySec || undefined,
                maxDelaySec: maxDelaySec || undefined,
                perDayLimit: perDayLimit || undefined,
                dispatchSortPolicy: dispatchSortPolicy || 'COMPANY_SEQUENTIAL'
            });
            showSnackbar(t('sequences.messages.configurationUpdated'), 'success');
            setSelectedSequence(res.data);
            setIsEditingConfig(false);
        } catch (err) {
            notifyError(err, 'errors.updateFailed');
        } finally {
            setIsSavingConfig(false);
        }
    };

    const handleAssignmentStatusToggle = async (assignmentId, status) => {
        try {
            await api.put(`/api/sequences/${selectedSequence._id}/assignments/${assignmentId}/status`, { status });
            showSnackbar(t('sequences.messages.assignmentStatusChanged', { status }), 'success');
            fetchAssignments(selectedSequence._id);
        } catch (err) {
            notifyError(err, 'sequences.messages.statusToggleFailed');
        }
    };

    const handleSmartSelection = ({ targetCount, maxPerCompany }) => {
        setIsAllSelected(false);

        let pool = [...assignments];
        let chosenIds = [];

        if (maxPerCompany) {
            const companyCounter = {};
            for (const a of pool) {
                const coId = a.companyId || 'unassigned';
                companyCounter[coId] = (companyCounter[coId] || 0) + 1;
                if (companyCounter[coId] <= maxPerCompany) {
                    chosenIds.push(a.assignmentId);
                    if (chosenIds.length >= targetCount) break;
                }
            }
        } else {
            chosenIds = pool.slice(0, targetCount).map(a => a.assignmentId);
        }

        setSelectedAssignmentIds(chosenIds);
        showSnackbar(t('sequences.messages.prospectsSelected', { count: chosenIds.length }), 'success');
    };

    const handleAssignmentRestart = async (assignmentId) => {
        openConfirm({
            title: t('sequences.confirm.restartTitle'),
            message: t('sequences.confirm.restartMessage'),
            confirmText: t('sequences.actions.restart'),
            onConfirm: async () => {
                try {
                    await api.post(`/api/sequences/${selectedSequence._id}/assignments/${assignmentId}/restart`);
                    showSnackbar(t('sequences.messages.contactRestarted'), 'success');
                    fetchAssignments(selectedSequence._id, assignmentsPagination.page);
                } catch (err) {
                    notifyError(err, 'sequences.messages.restartFailed');
                }
            }
        });
    };

    const handleAssignmentDelete = async (assignmentId) => {
        openConfirm({
            title: t('sequences.confirm.deleteContactTitle'),
            message: t('sequences.confirm.deleteContactMessage'),
            confirmText: t('actions.delete'),
            variant: 'destructive',
            onConfirm: async () => {
                try {
                    await api.post(`/api/sequences/${selectedSequence._id}/assignments/bulk-action`, {
                        assignmentIds: [assignmentId],
                        action: 'REMOVE'
                    });
                    showSnackbar(t('sequences.messages.contactDeleted'), 'success');
                    fetchAssignments(selectedSequence._id, assignmentsPagination.page);
                } catch (err) {
                    notifyError(err, 'sequences.messages.deleteFailed');
                }
            }
        });
    };

    const addStep = () => {
        const newStep = {
            stepNumber: editingSteps.length + 1,
            stepType: 'EMAIL',
            delayDays: 1,
            subjectTemplate: '',
            bodyTemplate: '',
            attachments: []
        };
        const nextSteps = [...editingSteps, newStep];
        setEditingSteps(nextSteps);
        setActiveStepIndex(nextSteps.length - 1);
        setActiveStepDraft(newStep);
        setActiveStepPreview(null);
        setActiveStepIsNew(true);
        setIsStepEditorOpen(true);
    };

    const insertStepVariable = (draftField, variable) => {
        const token = `{{${variable}}}`;
        const currentValue = activeStepDraft?.[draftField] || '';
        const spacer = currentValue && !currentValue.endsWith(' ') ? ' ' : '';
        setActiveStepDraft((prev) => ({
            ...(prev || {}),
            [draftField]: `${currentValue}${spacer}${token}`,
        }));
    };

    const handleStepImageUpload = async (file) => {
        setIsUploadingStepImage(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await api.post('/api/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return res.data.url;
        } catch (err) {
            console.error('Sequence step image upload error:', err);
            showSnackbar(t('errors.generic'), 'error');
            return null;
        } finally {
            setIsUploadingStepImage(false);
        }
    };

    const handleStepAttachmentUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploadingStepImage(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await api.post('/api/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const newAttachment = {
                filename: file.name,
                url: res.data.url,
                size: file.size,
                contentType: file.type || 'application/octet-stream'
            };

            setActiveStepDraft((prev) => ({
                ...(prev || {}),
                attachments: [...(prev?.attachments || []), newAttachment]
            }));
        } catch (err) {
            console.error('Sequence step attachment upload error:', err);
            showSnackbar(t('errors.generic'), 'error');
        } finally {
            setIsUploadingStepImage(false);
            e.target.value = null;
        }
    };

    const removeStepAttachment = (indexToRemove) => {
        setActiveStepDraft((prev) => ({
            ...(prev || {}),
            attachments: (prev?.attachments || []).filter((_, idx) => idx !== indexToRemove)
        }));
    };

  

    const openStepEditor = async (index, { isNew = false } = {}) => {
        const step = editingSteps[index];
        if (!step) return;

        const draft = {
            ...step,
            attachments: [...(step.attachments || [])]
        };

        setActiveStepIndex(index);
        setActiveStepDraft(draft);
        setActiveStepIsNew(isNew);
        setIsStepEditorOpen(true);
        setIsActiveStepPreviewLoading(true);
        const preview = await generateStepPreview(draft);
        setActiveStepPreview(preview);
        setIsActiveStepPreviewLoading(false);
    };

    const closeStepEditor = () => {
        setIsStepEditorOpen(false);
        setActiveStepIndex(null);
        setActiveStepDraft(null);
        setActiveStepPreview(null);
        setActiveStepIsNew(false);
        if (stepPreviewTimeoutRef.current) clearTimeout(stepPreviewTimeoutRef.current);
    };

    const discardStepEditorChanges = () => {
        if (activeStepIsNew && activeStepIndex !== null) {
            const nextSteps = editingSteps.filter((_, idx) => idx !== activeStepIndex)
                .map((step, idx) => ({ ...step, stepNumber: idx + 1 }));
            setEditingSteps(nextSteps);
        }
        closeStepEditor();
    };

  
    const requestCloseStepEditor = () => {
        const currentStep = activeStepIndex !== null ? editingSteps[activeStepIndex] : null;
        const isDirty = JSON.stringify({
            subjectTemplate: currentStep?.subjectTemplate || '',
            bodyTemplate: currentStep?.bodyTemplate || '',
            delayDays: currentStep?.delayDays ?? 0,
            attachments: currentStep?.attachments || [],
        }) !== JSON.stringify({
            subjectTemplate: activeStepDraft?.subjectTemplate || '',
            bodyTemplate: activeStepDraft?.bodyTemplate || '',
            delayDays: activeStepDraft?.delayDays ?? 0,
            attachments: activeStepDraft?.attachments || [],
        });

        if (!isDirty) {
            discardStepEditorChanges();
            return;
        }

        openConfirm({
            title: t('actions.cancel'),
            message: t('sequences.messages.unsavedChanges', { defaultValue: 'You have unsaved step changes. Discard them?' }),
            confirmText: t('actions.discard', { defaultValue: 'Discard' }),
            onConfirm: discardStepEditorChanges,
            variant: 'destructive'
        });
    };

    const saveActiveStepDraft = async () => {
        if (activeStepIndex === null || !activeStepDraft) return;
        const preview = await generateStepPreview(activeStepDraft);
        const nextSteps = [...editingSteps];
        nextSteps[activeStepIndex] = { ...activeStepDraft, stepNumber: activeStepIndex + 1 };
        setEditingSteps(nextSteps);
        setStepPreviews((prev) => ({ ...prev, [activeStepIndex]: preview }));
        setActiveStepPreview(preview);
        closeStepEditor();
    };

    const removeStep = (index) => {
        const _new = [...editingSteps];
        _new.splice(index, 1);
        // Correct step numbers
        const corrected = _new.map((s, i) => ({ ...s, stepNumber: i + 1 }));
        setEditingSteps(corrected);
        if (activeStepIndex === index) {
            closeStepEditor();
        }
    };

    const _renderList = () => (
        <PageShell>
            <PageHeader
                title={t('sequences.title')}
                subtitle={t('sequences.subtitle')}
                icon={Workflow}
                actions={
                    <button
                        onClick={openCreateModal}
                        className="bg-primary text-primary-foreground px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-primary/90 transition-colors font-bold text-sm shadow-sm"
                    >
                        <Plus className="h-4 w-4" /> {t('sequences.actions.newSequence')}
                    </button>
                }
            />

            <div className="bg-card border border-border rounded-[2.5rem] shadow-sm overflow-hidden mt-8">
                <DataTable
                    columns={[
                        {
                            header: t('sequences.table.sequence'),
                            accessor: 'name',
                            render: (s) => (
                                <div className="flex flex-col cursor-pointer" onClick={() => navigate(`/sequences/${s._id}`)}>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-sm hover:text-primary transition-colors">{s.name}</span>
                                        {s.isGraph && <div className="text-[9px] font-black tracking-widest uppercase border border-primary/20 bg-primary/5 text-primary rounded-md px-1.5 py-0.5">{t('sequences.labels.graph')}</div>}
                                    </div>
                                    <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mt-0.5">
                                        ID: {s._id}
                                    </span>
                                </div>
                            )
                        },
                        {
                            header: t('common.status'),
                            accessor: 'status',
                            render: (s) => (
                                <div className="flex flex-col gap-1">
                                    <StatusBadge
                                        variant={s.status === 'ACTIVE' ? 'success' : s.status === 'PAUSED' ? 'warning' : s.status === 'SCHEDULED' ? 'indigo' : 'default'}
                                        dot={s.status === 'ACTIVE'}
                                    >
                                        {s.status}
                                    </StatusBadge>
                                    {s.status === 'SCHEDULED' && s.scheduledAt && (
                                        <span className="text-[10px] text-muted-foreground font-bold flex items-center gap-1">
                                            <Clock className="w-2.5 h-2.5" /> {formatDateTime(s.scheduledAt, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }, i18n.resolvedLanguage)}
                                        </span>
                                    )}
                                </div>
                            )
                        },
                        {
                            header: t('sequences.table.enrolled'),
                            accessor: 'enrolled',
                            render: (s) => <span className="font-bold">{s.stats?.totalEnrolled || 0}</span>
                        },
                        {
                            header: t('status.active'),
                            accessor: 'active',
                            render: (s) => <span className="font-bold text-primary">{s.stats?.active || 0}</span>
                        },
                        {
                            header: t('status.sent'),
                            accessor: 'sent',
                            render: (s) => <span className="font-semibold text-muted-foreground">{s.stats?.sent || 0}</span>
                        },
                        {
                            header: t('sequences.table.replied'),
                            accessor: 'replied',
                            render: (s) => (
                                <div className="flex flex-col">
                                    <span className="font-bold text-green-600 dark:text-green-400">{s.stats?.replied || 0}</span>
                                    <span className="text-[10px] text-muted-foreground font-medium">{s.stats?.repliedBeforeReminder || 0} / {s.stats?.repliedAfterReminder || 0}</span>
                                </div>
                            )
                        }
                    ]}
                    data={sequences}
                    loading={loading}
                    emptyState={<EmptyState title={t('sequences.empty.title')} description={t('sequences.empty.description')} icon={Workflow} />}
                    pagination={{
                        page: 1,
                        limit: sequences.length || 10,
                        totalRecords: sequences.length,
                        totalPages: 1
                    }}
                />
            </div>
        </PageShell>
    );

    const _renderDetail = () => {
        const stats = sequenceAnalytics?.overall || {};
        const successRate = stats.sent > 0 ? ((stats.replies || stats.replied || 0) / stats.sent * 100).toFixed(1) : 0;
        const openRate = stats.sent > 0 ? ((stats.opened || 0) / stats.sent * 100).toFixed(1) : 0;

        return (
            <PageShell>
                <PageHeader
                    title={selectedSequence.name}
                    subtitle={t('sequences.detailSubtitle')}
                    icon={Workflow}
                    badge={
                        <StatusBadge
                            variant={selectedSequence.status === 'ACTIVE' ? 'success' : selectedSequence.status === 'PAUSED' ? 'warning' : selectedSequence.status === 'SCHEDULED' ? 'indigo' : 'default'}
                            dot={selectedSequence.status === 'ACTIVE'}
                        >
                            {selectedSequence.status}
                        </StatusBadge>
                    }
                    actions={
                        <div className="flex gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/sequences')}
                                className="rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground font-black text-[10px] uppercase tracking-widest px-4"
                            >
                                <ChevronLeft className="h-3 w-3 mr-1.5" /> {t('sequences.actions.backToBlueprint')}
                            </Button>
                            <Button
                                onClick={() => setIsEnrollModalOpen(true)}
                                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg shadow-blue-500/20"
                            >
                                <UserPlus className="h-4 w-4 mr-2" /> {t('sequences.actions.enroll')}
                            </Button>
                            {selectedSequence.status !== 'ACTIVE' ? (
                                <Button onClick={() => handleStatusChange(selectedSequence._id, 'ACTIVE')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                                    <Play className="h-4 w-4 mr-2" /> {t('sequences.actions.start')}
                                </Button>
                            ) : (
                                <Button onClick={() => handleStatusChange(selectedSequence._id, 'PAUSED')} className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl">
                                    <Pause className="h-4 w-4 mr-2" /> {t('sequences.actions.pause')}
                                </Button>
                            )}
                            <Button onClick={() => navigate(`/sequences/${selectedSequence._id}/edit`)} className="rounded-xl font-bold">
                                {selectedSequence.isGraph ? <Workflow className="h-4 w-4 mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
                                {selectedSequence.isGraph ? t('sequences.actions.graphEditor') : t('sequences.actions.editSteps')}
                            </Button>
                        </div>
                    }
                />

                {!hasSendingAccounts && (
                    <div className="mt-8 bg-destructive/10 border-2 border-dashed border-destructive/20 rounded-3xl p-6 flex items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <AlertTriangle className="w-6 h-6 text-destructive shrink-0 mt-1" />
                            <div>
                                <h4 className="text-destructive font-black text-sm uppercase tracking-tight">{t('sequences.alerts.sendingBlockedTitle')}</h4>
                                <p className="text-muted-foreground text-xs mt-1 font-medium max-w-xl">
                                    {t('sequences.alerts.sendingBlockedDescription')}
                                </p>
                            </div>
                        </div>
                        <Button variant="destructive" onClick={() => navigate('/mail-accounts')} className="rounded-xl">
                            {t('actions.connectAccount')}
                        </Button>
                    </div>
                )}

                {/* Live Orchestration Dashboard */}
                {selectedSequence.status === 'ACTIVE' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 animate-in fade-in slide-in-from-top-4 duration-500">
                        <Card className="rounded-[2.5rem] bg-gradient-to-br from-blue-600 to-indigo-700 text-white border-none shadow-xl shadow-blue-500/20">
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-3 bg-white/10 rounded-2xl">
                                            <Clock className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Sonraki Otomatik Gönderim</p>
                                            <h3 className="text-xl font-black mt-1">
                                                {selectedSequence.health?.nextExecution
                                                    ? formatDateTime(selectedSequence.health.nextExecution.time, {}, i18n.language)
                                                    : 'Sırada bekleyen kimse yok'}
                                            </h3>
                                            {selectedSequence.health?.nextExecution && (
                                                <p className="text-[10px] font-bold opacity-60 mt-0.5">
                                                    ({formatRelativeDistance(selectedSequence.health.nextExecution.time, {}, i18n.language)})
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <Badge className="bg-white/20 text-white border-none font-bold">CANLI KUYRUK</Badge>
                                </div>
                                {selectedSequence.health?.nextExecution && (
                                    <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">
                                                {selectedSequence.health.nextExecution.contactName.charAt(0)}
                                            </div>
                                            <span className="text-xs font-bold">{selectedSequence.health.nextExecution.contactName}</span>
                                        </div>
                                        <div className="text-[10px] font-black uppercase tracking-widest opacity-60">
                                            Adım: {selectedSequence.health.nextExecution.step}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="rounded-[2.5rem] bg-white dark:bg-slate-900 border-2 border-primary/10 shadow-sm">
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-3 bg-primary/10 rounded-2xl">
                                            <Zap className="w-6 h-6 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Bugün Tahmini Gönderilecek</p>
                                            <h3 className="text-2xl font-black mt-1 text-foreground">
                                                {selectedSequence.health?.estimatedToday || 0} <span className="text-sm font-medium text-muted-foreground ml-2">Bekleyen Mail</span>
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <span className="text-[9px] font-black uppercase tracking-widest text-green-600 mb-1">Havuz Durumu</span>
                                        <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200 font-bold uppercase tracking-tighter">Aktif Akış</Badge>
                                    </div>
                                </div>
                                <div className="mt-6 pt-4 border-t border-border/40 flex items-center gap-4">
                                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                        <div className="h-full bg-primary" style={{ width: `${Math.min(100, (selectedSequence.health?.estimatedToday || 0) * 10)}%` }} />
                                    </div>
                                    <span className="text-[10px] font-bold text-muted-foreground">Kapasite: 1k/gün</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* KPI Overview */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                    <StatCard
                        title={t('sequences.metrics.sentVolume')}
                        value={stats.sent || 0}
                        icon={Mail}
                        description={t('sequences.metrics.sentVolumeDescription')}
                        trend="+12%" // Placeholder for real trend if available
                    />
                    <StatCard
                        title={t('sequences.metrics.openRate')}
                        value={`${openRate}%`}
                        icon={Eye}
                        description={t('sequences.metrics.uniqueOpens', { count: stats.opened || 0 })}
                        color="blue"
                    />
                    <StatCard
                        title={t('sequences.metrics.replyRate')}
                        value={`${successRate}%`}
                        icon={MessageSquare}
                        description={t('sequences.metrics.totalReplies', { count: stats.replies || stats.replied || 0 })}
                        color="purple"
                    />
                    <StatCard
                        title={t('sequences.metrics.successMetric')}
                        value={`${successRate}%`}
                        icon={Trophy}
                        description={t('sequences.metrics.successMetricDescription')}
                        color="emerald"
                    />
                </div>

                {/* General Info Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
                    <div className="lg:col-span-2">
                        <Card className="rounded-[2rem] overflow-hidden border-border/60">
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-primary" /> General Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Basic Info */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-muted/20 p-3 rounded-xl">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Status</p>
                                        <p className="text-sm font-bold text-foreground mt-1">{selectedSequence.status}</p>
                                    </div>
                                    <div className="bg-muted/20 p-3 rounded-xl">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Created</p>
                                        <p className="text-sm font-bold text-foreground mt-1">{selectedSequence.createdAt ? formatDate(selectedSequence.createdAt, { dateStyle: 'medium' }, i18n.language) : '-'}</p>
                                    </div>
                                    <div className="bg-muted/20 p-3 rounded-xl">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Updated</p>
                                        <p className="text-sm font-bold text-foreground mt-1">{selectedSequence.updatedAt ? formatDate(selectedSequence.updatedAt, { dateStyle: 'medium' }, i18n.language) : '-'}</p>
                                    </div>
                                    <div className="bg-muted/20 p-3 rounded-xl">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Format</p>
                                        <p className="text-sm font-bold text-foreground mt-1">{selectedSequence.isGraph ? 'Graph' : 'Linear'}</p>
                                    </div>
                                </div>

                                {/* Stats */}
                                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                                    <div className="bg-muted/10 p-3 rounded-xl text-center">
                                        <p className="text-lg font-black text-primary">{stats.sent || 0}</p>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mt-1">Sent</p>
                                    </div>
                                    <div className="bg-muted/10 p-3 rounded-xl text-center">
                                        <p className="text-lg font-black text-orange-500">{stats.opened || 0}</p>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mt-1">Opened</p>
                                    </div>
                                    <div className="bg-muted/10 p-3 rounded-xl text-center">
                                        <p className="text-lg font-black text-purple-500">{stats.clicked || 0}</p>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mt-1">Clicked</p>
                                    </div>
                                    <div className="bg-muted/10 p-3 rounded-xl text-center">
                                        <p className="text-lg font-black text-blue-500">{stats.replied || stats.replies || 0}</p>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mt-1">Replied</p>
                                    </div>
                                    <div className="bg-muted/10 p-3 rounded-xl text-center">
                                        <p className="text-lg font-black text-red-500">{stats.bounced || 0}</p>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mt-1">Bounced</p>
                                    </div>
                                    <div className="bg-muted/10 p-3 rounded-xl text-center">
                                        <p className="text-lg font-black text-emerald-500">{stats.finished || 0}</p>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mt-1">Finished</p>
                                    </div>
                                </div>

                                {/* Sending Source */}
                                <div className="pt-4 border-t border-border/40">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3">Sending Source</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-muted/20 p-3 rounded-xl">
                                            <p className="text-[10px] font-bold text-muted-foreground">Source Type</p>
                                            <p className="text-sm font-bold text-foreground mt-0.5">{selectedSequence.sendingSourceType || 'ACCOUNT'}</p>
                                        </div>
                                        <div className="bg-muted/20 p-3 rounded-xl">
                                            <p className="text-[10px] font-bold text-muted-foreground">Sender</p>
                                            <p className="text-sm font-bold text-foreground mt-0.5">{getSequenceSenderDisplay(selectedSequence, mailAccounts, senderPools, t)}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Delivery Settings */}
                                <div className="pt-4 border-t border-border/40">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3">Delivery Settings</p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div className="bg-muted/20 p-3 rounded-xl">
                                            <p className="text-[10px] font-bold text-muted-foreground">Timezone</p>
                                            <p className="text-sm font-bold text-foreground mt-0.5">{selectedSequence.timezone || 'UTC'}</p>
                                        </div>
                                        <div className="bg-muted/20 p-3 rounded-xl">
                                            <p className="text-[10px] font-bold text-muted-foreground">Window</p>
                                            <p className="text-sm font-bold text-foreground mt-0.5">{(selectedSequence.sendingWindowStart || '09:00').slice(0,5)} - {(selectedSequence.sendingWindowEnd || '17:30').slice(0,5)}</p>
                                        </div>
                                        <div className="bg-muted/20 p-3 rounded-xl">
                                            <p className="text-[10px] font-bold text-muted-foreground">Delay (sec)</p>
                                            <p className="text-sm font-bold text-foreground mt-0.5">{(selectedSequence.minDelaySec || 0)}s - {(selectedSequence.maxDelaySec || 0)}s</p>
                                        </div>
                                        <div className="bg-muted/20 p-3 rounded-xl">
                                            <p className="text-[10px] font-bold text-muted-foreground">Per Day</p>
                                            <p className="text-sm font-bold text-foreground mt-0.5">{selectedSequence.perDayLimit > 0 ? selectedSequence.perDayLimit : 'Unlimited'}</p>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <span className="text-[10px] font-bold text-muted-foreground">Working Days:</span>
                                        {(selectedSequence.workingDays || [1,2,3,4,5]).map(d => (
                                            <span key={d} className="text-[10px] font-black uppercase bg-muted/30 px-2 py-0.5 rounded">{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d]}</span>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="space-y-6">
                        <Card className="rounded-[2rem] overflow-hidden border-border/60">
                            <CardHeader>
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-primary" /> Quick Info
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-bold text-muted-foreground">Start Mode</span>
                                    <span className="text-xs font-bold">{selectedSequence.startMode || 'IMMEDIATE'}</span>
                                </div>
                                {selectedSequence.startMode === 'SCHEDULED' && selectedSequence.scheduledAt && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-[10px] font-bold text-muted-foreground">Scheduled At</span>
                                        <span className="text-xs font-bold text-primary">{formatDateTime(selectedSequence.scheduledAt, {}, i18n.language)}</span>
                                    </div>
                                )}
                                {selectedSequence.endAt && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-[10px] font-bold text-muted-foreground">Ends At</span>
                                        <span className="text-xs font-bold text-destructive">{formatDate(selectedSequence.endAt, { dateStyle: 'medium' }, i18n.language)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-bold text-muted-foreground">Next Dispatch</span>
                                    <span className="text-xs font-bold">{selectedSequence.nextExpectedDispatchAt ? formatDateTime(selectedSequence.nextExpectedDispatchAt, {}, i18n.language) : '-'}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-bold text-muted-foreground">Last Dispatch</span>
                                    <span className="text-xs font-bold">{selectedSequence.lastDispatchAt ? formatDateTime(selectedSequence.lastDispatchAt, {}, i18n.language) : '-'}</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10 items-start">
                    <div className="lg:col-span-2 space-y-8">
                        {/* 1. Sending Source Configuration */}
                        <Card className="rounded-[2rem] overflow-hidden border-border/60">
                            <CardHeader className="pb-4 flex flex-row items-center justify-between">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <ShieldCheck className="w-5 h-5 text-primary" /> Sending Intelligence
                                </CardTitle>
                                {!isEditingConfig ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setConfigForm({
                                                sendingSourceType: selectedSequence.sendingSourceType || 'ACCOUNT',
                                                defaultSenderAccountId: selectedSequence.defaultSenderAccountId?._id || selectedSequence.defaultSenderAccountId || '',
                                                senderPoolId: selectedSequence.senderPoolId?._id || selectedSequence.senderPoolId || ''
                                            });
                                            setIsEditingConfig(true);
                                        }}
                                        className="rounded-xl font-bold text-[10px] uppercase tracking-widest text-primary"
                                    >
                                        Edit Configuration
                                    </Button>
                                ) : (
                                    <div className="flex gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setIsEditingConfig(false)}
                                            className="rounded-xl font-bold text-[10px] uppercase tracking-widest text-muted-foreground"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={handleSaveConfig}
                                            disabled={isSavingConfig}
                                            className="rounded-xl font-bold text-[10px] uppercase tracking-widest bg-emerald-600 hover:bg-emerald-700 text-white"
                                        >
                                            {isSavingConfig ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                {isEditingConfig ? (
                                    <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Resolution Mode</label>
                                                <div className="grid grid-cols-2 gap-2 p-1 bg-muted/30 rounded-xl border border-border/40">
                                                    <button
                                                        onClick={() => setConfigForm(prev => ({ ...prev, sendingSourceType: 'ACCOUNT' }))}
                                                        className={cn("py-2 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all", configForm.sendingSourceType === 'ACCOUNT' ? "bg-white text-primary shadow-sm" : "text-muted-foreground hover:text-slate-900")}
                                                    >
                                                        Specific Account
                                                    </button>
                                                    <button
                                                        onClick={() => setConfigForm(prev => ({ ...prev, sendingSourceType: 'POOL' }))}
                                                        className={cn("py-2 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all", configForm.sendingSourceType === 'POOL' ? "bg-white text-indigo-600 shadow-sm" : "text-muted-foreground hover:text-slate-900")}
                                                    >
                                                        Sender Pool
                                                    </button>
                                                </div>
                                            </div>

                                            {configForm.sendingSourceType === 'ACCOUNT' ? (
                                                <div className="space-y-1.5 animate-in fade-in slide-in-from-left-2 duration-300">
                                                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Primary Identity</label>
                                                    <Select
                                                        value={configForm.defaultSenderAccountId}
                                                        onValueChange={(val) => setConfigForm(prev => ({ ...prev, defaultSenderAccountId: val === 'none' ? '' : val }))}
                                                    >
                                                        <SelectTrigger className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold h-12 px-5 transition-all text-xs">
                                                            <SelectValue placeholder="-- Select Sender --" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                                                            <SelectItem value="none" className="font-bold cursor-pointer hover:bg-muted focus:bg-primary/10 transition-colors py-3">
                                                                No default (select later)
                                                            </SelectItem>
                                                            {mailAccounts.map(acc => (
                                                                <SelectItem key={acc._id} value={acc._id} className="font-bold cursor-pointer hover:bg-muted focus:bg-primary/10 transition-colors py-3">
                                                                    {acc.from || acc.user} <span className="opacity-50 text-[10px] ml-1 font-medium italic">({acc.user})</span>
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            ) : (
                                                <div className="space-y-1.5 animate-in fade-in slide-in-from-right-2 duration-300">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Smart Pool</label>
                                                        <button
                                                            type="button"
                                                            onClick={() => openPoolBuilder('CONFIG')}
                                                            className="text-[10px] font-black uppercase tracking-widest text-primary hover:underline"
                                                        >
                                                            Create Pool
                                                        </button>
                                                    </div>
                                                    <Select
                                                        value={configForm.senderPoolId}
                                                        onValueChange={(val) => setConfigForm(prev => ({ ...prev, senderPoolId: val }))}
                                                    >
                                                        <SelectTrigger className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-indigo-500 font-bold h-12 px-5 transition-all text-xs text-indigo-700">
                                                            <SelectValue placeholder="-- Select Sender Pool --" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                                                            {senderPools.length === 0 ? (
                                                                <div className="p-4 text-center">
                                                                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">No Pools Found</p>
                                                                    <a href="/sender-pools" className="text-[10px] font-black uppercase tracking-widest text-primary hover:underline">Configure Pools →</a>
                                                                </div>
                                                            ) : (
                                                                senderPools.map(pool => (
                                                                    <SelectItem key={pool._id} value={pool._id} className="font-bold cursor-pointer hover:bg-muted focus:bg-indigo-500/10 transition-colors py-3 text-indigo-900">
                                                                        {pool.name} <span className="opacity-50 text-[10px] ml-1 font-medium italic">({pool.mailAccountIds?.length || 0} accounts)</span>
                                                                    </SelectItem>
                                                                ))
                                                            )}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border/40">
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Default Delivery Timezone (Fallback)</label>
                                                <p className="text-[9px] text-muted-foreground -mt-1 mb-1">Used if contact's local timezone cannot be resolved from their location.</p>
                                                <TimezoneSelector value={timezone} onChange={setTimezone} />
                                            </div>                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Working Days</label>
                                                <div className="flex flex-wrap gap-2">
                                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                                                        <button
                                                            key={day}
                                                            type="button"
                                                            onClick={() => setWorkingDays(prev => prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx].sort())}
                                                            className={cn(
                                                                "px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                                                                workingDays.includes(idx) ? "bg-primary text-white shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"
                                                            )}
                                                        >
                                                            {day}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-6 pt-4 border-t border-border/40">
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Window Start</label>
                                                <input
                                                    type="time"
                                                    value={sendingWindowStart}
                                                    onChange={e => setSendingWindowStart(e.target.value)}
                                                    className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3 px-5 transition-all text-xs"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Window End</label>
                                                <input
                                                    type="time"
                                                    value={sendingWindowEnd}
                                                    onChange={e => setSendingWindowEnd(e.target.value)}
                                                    className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3 px-5 transition-all text-xs"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border/40">
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Start Mode</label>
                                                <Select value={startMode} onValueChange={setStartMode}>
                                                    <SelectTrigger className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-indigo-500 font-bold h-12 px-5 transition-all text-xs">
                                                        <SelectValue placeholder="Select start mode" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                                                        <SelectItem value="IMMEDIATE" className="font-bold cursor-pointer hover:bg-muted focus:bg-indigo-500/10 transition-colors py-3 text-indigo-900">Immediate</SelectItem>
                                                        <SelectItem value="SCHEDULED" className="font-bold cursor-pointer hover:bg-muted focus:bg-indigo-500/10 transition-colors py-3 text-indigo-900">Scheduled</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">End At (Optional)</label>
                                                <input
                                                    type="datetime-local"
                                                    value={endAt}
                                                    onChange={e => setEndAt(e.target.value)}
                                                    className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3 px-5 transition-all text-xs"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-6 pt-4 border-t border-border/40">
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Min Delay (sec)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={minDelaySec}
                                                    onChange={e => setMinDelaySec(parseInt(e.target.value) || 0)}
                                                    className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3 px-5 transition-all text-xs"
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Max Delay (sec)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={maxDelaySec}
                                                    onChange={e => setMaxDelaySec(parseInt(e.target.value) || 0)}
                                                    className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3 px-5 transition-all text-xs"
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Per Day Limit</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={perDayLimit}
                                                    onChange={e => setPerDayLimit(parseInt(e.target.value) || 0)}
                                                    className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3 px-5 transition-all text-xs"
                                                    placeholder="0 (unlimited)"
                                                />
                                                <p className="text-[9px] text-muted-foreground">0 means unlimited</p>
                                            </div>
                                            <div className="col-span-full space-y-1.5 mt-2">
                                                 <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Gönderim Sıralaması / Dispatch Order</label>
                                                 <select
                                                     value={dispatchSortPolicy}
                                                     onChange={e => setDispatchSortPolicy(e.target.value)}
                                                     className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3 px-5 transition-all text-xs"
                                                 >
                                                     <option value="COMPANY_SEQUENTIAL">🏢 Şirket Bazlı Sıralı (Bir Şirket Bitince Diğerine)</option>
                                                     <option value="COMPANY_INTERLEAVED">🔄 Şirketler Arası Dönüşümlü (Spam Korumalı)</option>
                                                     <option value="SENIORITY_FIRST">👑 Kıdem / Unvan Öncelikli (CEO/VP İlk Sırada)</option>
                                                     <option value="DEFAULT">⏱️ Eklenme Sırasına Göre</option>
                                                 </select>
                                            </div>
                                        </div>

                                        <p className="text-[10px] text-muted-foreground font-medium italic leading-relaxed bg-slate-50 p-3 border-2 border-dashed rounded-xl">
                                            Updating the configuration affects future enrollments and existing participants that have not yet resolved a sender for their current step natively.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        {/* Row 1: Status & Dates */}
                                        <div className="flex flex-wrap gap-6 items-center">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Status</span>
                                                <StatusBadge
                                                    variant={selectedSequence.status === 'ACTIVE' ? 'success' : selectedSequence.status === 'PAUSED' ? 'warning' : selectedSequence.status === 'SCHEDULED' ? 'indigo' : 'default'}
                                                    dot={selectedSequence.status === 'ACTIVE'}
                                                >
                                                    {selectedSequence.status}
                                                </StatusBadge>
                                            </div>
                                            <div className="h-10 w-[1px] bg-border/60" />
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Created</span>
                                                <span className="text-sm font-bold">
                                                    {selectedSequence.createdAt ? new Date(selectedSequence.createdAt).toLocaleDateString('tr-TR') : '-'}
                                                </span>
                                            </div>
                                            <div className="h-10 w-[1px] bg-border/60" />
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Updated</span>
                                                <span className="text-sm font-bold">
                                                    {selectedSequence.updatedAt ? new Date(selectedSequence.updatedAt).toLocaleDateString('tr-TR') : '-'}
                                                </span>
                                            </div>
                                            {selectedSequence.scheduledAt && (
                                                <>
                                                    <div className="h-10 w-[1px] bg-border/60" />
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Scheduled At</span>
                                                        <span className="text-sm font-bold">
                                                            {new Date(selectedSequence.scheduledAt).toLocaleString('tr-TR')}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            {selectedSequence.endAt && (
                                                <>
                                                    <div className="h-10 w-[1px] bg-border/60" />
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">End At</span>
                                                        <span className="text-sm font-bold">
                                                            {new Date(selectedSequence.endAt).toLocaleString('tr-TR')}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* Row 2: Stats */}
                                        <div className="flex flex-wrap gap-6 items-center">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Total Stats</span>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs font-bold">Sent: <span className="text-blue-600">{selectedSequence.stats?.sent || 0}</span></span>
                                                    <span className="text-xs font-bold">Opened: <span className="text-green-600">{selectedSequence.stats?.opened || 0}</span></span>
                                                    <span className="text-xs font-bold">Clicked: <span className="text-purple-600">{selectedSequence.stats?.clicked || 0}</span></span>
                                                    <span className="text-xs font-bold">Replied: <span className="text-emerald-600">{selectedSequence.stats?.replied || 0}</span></span>
                                                    <span className="text-xs font-bold">Bounced: <span className="text-red-600">{selectedSequence.stats?.bounced || 0}</span></span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Row 3: Sending Source */}
                                        <div className="flex flex-wrap gap-6 items-center">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Strategy</span>
                                                <div className="flex items-center gap-2">
                                                    {selectedSequence.sendingSourceType === 'POOL' ? (
                                                        <Badge variant="secondary" className="bg-purple-500/10 text-purple-700 hover:bg-purple-500/20 px-3 py-1 rounded-full border-none font-bold text-[9px] uppercase tracking-widest">
                                                            <Users className="w-3 h-3 mr-1.5" /> Sender Pool
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="secondary" className="bg-blue-500/10 text-blue-700 hover:bg-blue-500/20 px-3 py-1 rounded-full border-none font-bold text-[9px] uppercase tracking-widest">
                                                            <Mail className="w-3 h-3 mr-1.5" /> Single Account
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="h-10 w-[1px] bg-border/60" />
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Source Details</span>
                                                <span className="text-sm font-bold truncate max-w-[200px]">
                                                    {getSequenceSenderDisplay(selectedSequence, mailAccounts, senderPools)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Row 4: Timezone & Scheduling */}
                                        <div className="flex flex-wrap gap-6 items-center">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Timezone</span>
                                                <span className="text-sm font-bold">{selectedSequence.timezone || 'UTC'}</span>
                                            </div>
                                            <div className="h-10 w-[1px] bg-border/60" />
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Working Days</span>
                                                <div className="flex gap-1">
                                                    {(selectedSequence.workingDays || [1,2,3,4,5]).map(day => (
                                                        <span key={day} className="px-2 py-0.5 bg-muted rounded text-[10px] font-bold">{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][day]}</span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="h-10 w-[1px] bg-border/60" />
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Window</span>
                                                <span className="text-sm font-bold">{selectedSequence.sendingWindowStart || '09:00'} - {selectedSequence.sendingWindowEnd || '17:30'}</span>
                                            </div>
                                            {selectedSequence.perDayLimit > 0 && (
                                                <>
                                                    <div className="h-10 w-[1px] bg-border/60" />
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Per Day Limit</span>
                                                        <span className="text-sm font-bold">{selectedSequence.perDayLimit}</span>
                                                    </div>
                                                </>
                                            )}
                                            {selectedSequence.minDelaySec > 0 || selectedSequence.maxDelaySec > 0 ? (
                                                <>
                                                    <div className="h-10 w-[1px] bg-border/60" />
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Delay Range</span>
                                                        <span className="text-sm font-bold">
                                                            {selectedSequence.minDelaySec || 0}s - {selectedSequence.maxDelaySec || 0}s
                                                        </span>
                                                    </div>
                                                </>
                                            ) : null}
                                        </div>

                                        {/* Row 5: Execution Engine */}
                                        <div className="flex flex-wrap gap-6 items-center">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Execution Engine</span>
                                                <div className="flex items-center gap-2">
                                                    {selectedSequence.isGraph ? (
                                                        <span className="text-xs font-bold text-blue-600 flex items-center gap-1.5 uppercase tracking-tight">
                                                            <Zap className="w-3.5 h-3.5" /> Flow-Graph Native
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs font-bold text-muted-foreground flex items-center gap-1.5 uppercase tracking-tight">
                                                            <ListTree className="w-3.5 h-3.5" /> Linear Orchestrator
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {selectedSequence.nextExpectedDispatchAt && (
                                                <>
                                                    <div className="h-10 w-[1px] bg-border/60" />
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Next Expected</span>
                                                        <span className="text-sm font-bold">
                                                            {new Date(selectedSequence.nextExpectedDispatchAt).toLocaleString('tr-TR')}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            {selectedSequence.lastDispatchAt && (
                                                <>
                                                    <div className="h-10 w-[1px] bg-border/60" />
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-60">Last Dispatch</span>
                                                        <span className="text-sm font-bold">
                                                            {new Date(selectedSequence.lastDispatchAt).toLocaleString('tr-TR')}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* 2. Outreach Journey */}
                        <Card className="rounded-[2rem] overflow-hidden border-border/60">
                            <CardHeader className="flex flex-row items-center justify-between pb-6">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Workflow className="w-5 h-5 text-primary" /> Logic Blueprint
                                </CardTitle>
                                {!selectedSequence.isGraph && (
                                    <Badge variant="outline" className="text-[10px] font-black uppercase tracking-widest">
                                        {selectedSequence.steps?.length || 0} Linear Stages
                                    </Badge>
                                )}
                            </CardHeader>
                            <CardContent>
                                {selectedSequence.isGraph ? (
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="text-[10px] font-black uppercase tracking-widest text-primary border-primary/20">
                                                    Interactive Blueprint
                                                </Badge>
                                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-60">
                                                    Path Extraction Active
                                                </span>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => navigate(`/sequences/${selectedSequence._id}/edit`)}
                                                className="h-7 text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-primary/5 hover:text-primary transition-colors"
                                            >
                                                View Flow Engine <ArrowRight className="w-3 h-3 ml-1.5" />
                                            </Button>
                                        </div>

                                        {getGraphSequenceTimeline(selectedSequence.graph).length === 0 ? (
                                            <div className="p-8 border-2 border-dashed border-border/60 rounded-3xl text-center bg-muted/5">
                                                <div className="inline-flex p-4 rounded-2xl bg-primary/5 mb-4">
                                                    <Workflow className="w-10 h-10 text-primary opacity-40" />
                                                </div>
                                                <h4 className="font-black text-lg tracking-tight">Conditional Flow Architecture</h4>
                                                <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto font-medium leading-relaxed">
                                                    This sequence uses dynamic logic branches. Inspect the graph editor to see the full topology.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="relative pl-8 border-l-2 border-dashed border-primary/20 ml-2 space-y-6">
                                                {getGraphSequenceTimeline(selectedSequence.graph).map((stage, idx) => (
                                                    <div key={idx} className="relative group">
                                                        <div className="absolute -left-[3.15rem] top-0 w-8 h-8 rounded-xl bg-background border-2 border-primary flex items-center justify-center text-primary font-black text-xs shadow-sm z-10 transition-transform group-hover:scale-110">
                                                            {idx + 1}
                                                        </div>
                                                        <div className="p-4 border border-border/60 bg-card rounded-2xl group-hover:border-primary/40 transition-all shadow-sm group-hover:shadow-md">
                                                            <div className="flex justify-between items-start mb-2">
                                                                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-primary/70">
                                                                    <Mail className="w-3.5 h-3.5" /> {stage.label}
                                                                </div>
                                                                <Badge variant="secondary" className="text-[9px] font-black uppercase tracking-widest bg-muted/50 border-none">
                                                                    <Clock className="w-2.5 h-2.5 mr-1" /> {idx === 0 ? "Immediate" : `Wait ${stage.delayDays}d`}
                                                                </Badge>
                                                            </div>
                                                            <h5 className="text-sm font-bold tracking-tight truncate">
                                                                {stage.subject}
                                                            </h5>
                                                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                <div className="rounded-xl bg-muted/30 border border-border/50 p-3">
                                                                    <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">Trigger</p>
                                                                    <p className="text-[11px] font-semibold text-foreground">
                                                                        {idx === 0 ? 'Contact enrollment enters the graph' : `Previous branch completes, then waits ${stage.delayDays} day(s)`}
                                                                    </p>
                                                                </div>
                                                                <div className="rounded-xl bg-muted/30 border border-border/50 p-3">
                                                                    <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">Action</p>
                                                                    <p className="text-[11px] font-semibold text-foreground">
                                                                        Sends the message shown above if the branch remains valid.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                <div className="relative group pl-2">
                                                    <div className="absolute -left-[2.85rem] top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-border/40 border-4 border-background z-10" />
                                                    <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground opacity-40 italic">
                                                        Follow-up logical termination or stop branch
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (!selectedSequence.steps || selectedSequence.steps.length === 0) ? (
                                    <div className="py-16 text-center flex flex-col items-center">
                                        <div className="p-4 rounded-full bg-muted/10 mb-4">
                                            <Plus className="w-10 h-10 text-muted-foreground/30" />
                                        </div>
                                        <p className="text-sm text-muted-foreground font-bold italic">No steps defined. Add outreach logic to start.</p>
                                        <Button onClick={() => navigate(`/sequences/${selectedSequence._id}/edit`)} variant="link" className="mt-2">Configure Now</Button>
                                    </div>
                                ) : (
                                    <div className="relative pl-8 border-l-2 border-dashed border-primary/20 ml-2 space-y-6">
                                        {selectedSequence.steps.map((step, idx) => (
                                            (() => {
                                                const details = getLinearStepDetails(step, idx);
                                                return (
                                                    <div key={idx} className="relative group">
                                                        <div className="absolute -left-[3.15rem] top-0 w-8 h-8 rounded-xl bg-background border-2 border-primary flex items-center justify-center text-primary font-black text-xs shadow-sm z-10">
                                                            {step.stepNumber}
                                                        </div>
                                                        <div className="p-4 border border-border/60 bg-card rounded-2xl group-hover:border-primary/40 transition-all shadow-sm">
                                                            <div className="flex justify-between items-start mb-2">
                                                                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-primary/70">
                                                                    <Mail className="w-3.5 h-3.5" /> Step {idx + 1}
                                                                </div>
                                                                <Badge variant="secondary" className="text-[9px] font-black uppercase tracking-widest bg-muted/50 border-none">
                                                                    <Clock className="w-2.5 h-2.5 mr-1" /> {step.delayDays}d Wait
                                                                </Badge>
                                                            </div>
                                                            <h5 className="text-sm font-bold tracking-tight truncate">{details.title}</h5>
                                                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                <div className="rounded-xl bg-muted/30 border border-border/50 p-3">
                                                                    <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">Trigger</p>
                                                                    <p className="text-[11px] font-semibold text-foreground">{details.trigger}</p>
                                                                </div>
                                                                <div className="rounded-xl bg-muted/30 border border-border/50 p-3">
                                                                    <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">Explanation</p>
                                                                    <p className="text-[11px] font-medium text-muted-foreground line-clamp-3">{details.description}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <div className="space-y-8">
                        {/* 4. Deliverability Sidebar */}
                        <Card className="rounded-[2.5rem] overflow-hidden border-border/60 shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-red-500" /> Deliverability
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between p-4 bg-emerald-500/5 rounded-2xl border border-emerald-500/10">
                                    <div className="flex items-center gap-2 text-xs font-bold text-emerald-700">
                                        <ShieldCheck className="w-4 h-4" /> Healthy Executions
                                    </div>
                                    <span className="font-black text-lg">{selectedSequence.health?.active || 0}</span>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-red-500/5 rounded-2xl border border-red-500/10">
                                    <div className="flex items-center gap-2 text-xs font-bold text-red-700">
                                        <Zap className="w-4 h-4" /> Stuck / Recovering
                                    </div>
                                    <span className="font-black text-lg">{selectedSequence.health?.stuckExecuting || 0}</span>
                                </div>

                                <div className="mt-8 pt-6 border-t border-border/60">
                                    <h5 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60 mb-4 px-1">Drop-off Reasons</h5>
                                    <div className="space-y-2 px-1">
                                        {sequenceAnalytics?.stopReasons?.length
                                            ? sequenceAnalytics.stopReasons.slice(0, 5).map((r, idx) => (
                                                <div key={idx} className="flex justify-between items-center text-xs">
                                                    <span className="font-bold text-muted-foreground truncate max-w-[140px]">{r.reason}</span>
                                                    <Badge variant="secondary" className="px-1.5 py-0 rounded text-[10px] font-black">{r.count}</Badge>
                                                </div>
                                            ))
                                            : <div className="text-center py-6 italic text-[10px] text-muted-foreground font-bold uppercase opacity-40 border-2 border-dashed rounded-2xl">Clean Pipeline</div>}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* 5. Ops Pipeline Sidebar */}
                        <Card className="rounded-[2.5rem] overflow-hidden border-border/60 shadow-sm bg-gradient-to-br from-card to-primary/5">
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Target className="w-5 h-5 text-primary" /> Task Queue
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-3 mb-6">
                                    <div className="p-4 bg-background border rounded-2xl shadow-sm text-center">
                                        <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground block mb-1">Open Tasks</span>
                                        <span className="text-2xl font-black">{selectedSequence.health?.manualTasks || 0}</span>
                                    </div>
                                    <div className="p-4 bg-background border rounded-2xl shadow-sm text-center">
                                        <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground block mb-1">Success</span>
                                        <span className="text-2xl font-black">{selectedSequence.stats?.replied || 0}</span>
                                    </div>
                                </div>
                                <div className="mt-2 text-center">
                                    <Button variant="ghost" className="w-full rounded-xl text-[10px] font-black uppercase tracking-widest py-6 border border-border/40 hover:bg-white shadow-sm" onClick={() => navigate('/tasks')}>
                                        View Full Task Queue <ChevronRight className="w-3 h-3 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* 3. Enrollments Table - NOW FULL WIDTH BELOW */}
                    <Card className="rounded-[2rem] overflow-hidden border-border/60 lg:col-span-3 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <CardHeader className="flex flex-row items-center justify-between pb-4">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Users className="w-5 h-5 text-emerald-600" /> Active Prospects
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-700 font-black border-none">
                                    {assignments.length} Total
                                </Badge>
                            </div>
                        </CardHeader>
                        <CardContent className="px-0">
                            {/* Search and Filters */}
                            <div className="mx-6 mb-4 flex flex-wrap gap-3 items-center">
                                <div className="flex-1 min-w-[200px]">
                                    <input
                                        type="text"
                                        placeholder="Search by name or email..."
                                        value={assignmentSearch}
                                        onChange={e => setAssignmentSearch(e.target.value)}
                                        className="w-full h-10 px-4 rounded-xl bg-muted/30 border-2 border-border/50 text-sm font-medium focus:border-primary outline-none transition-all"
                                    />
                                </div>
                                <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); fetchAssignments(selectedSequence._id, 1, val); }}>
                                    <SelectTrigger className="h-10 w-[180px] rounded-xl bg-muted/30 border-2 border-border/50 text-sm">
                                        <SelectValue placeholder="Filter by status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ALL">All Statuses</SelectItem>
                                        <SelectItem value="ACTIVE">Active</SelectItem>
                                        <SelectItem value="PENDING">Pending</SelectItem>
                                        <SelectItem value="PAUSED">Paused</SelectItem>
                                        <SelectItem value="WAIT_FOR_MEETING">Waiting Meeting</SelectItem>
                                        <SelectItem value="MANUAL_TASK_WAIT">Manual Task</SelectItem>
                                        <SelectItem value="REPLIED">Replied</SelectItem>
                                        <SelectItem value="FINISHED">Finished</SelectItem>
                                        <SelectItem value="FAILED">Failed</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => { setAssignmentSearch(''); setStatusFilter('ALL'); fetchAssignments(selectedSequence._id, 1, 'ALL'); }}
                                    className="h-10 rounded-xl text-[10px] font-black uppercase tracking-widest"
                                >
                                    Clear Filters
                                </Button>
                            </div>

                            <div className="mx-6 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">Trigger Logic</p>
                                    <p className="text-[11px] font-medium text-foreground">Shows why a contact is waiting, paused, failed, or ready for the next step.</p>
                                </div>
                                <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">Next Action</p>
                                    <p className="text-[11px] font-medium text-foreground">Explains the next expected execution time or what the operator needs to do.</p>
                                </div>
                                <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">Source</p>
                                    <p className="text-[11px] font-medium text-foreground">Shows how the contact originally entered this sequence.</p>
                                </div>
                            </div>
                            <DataTable
                                headerExtra={
                                    <div className="flex items-center gap-2">
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" size="sm" className="h-10 px-4 rounded-xl border-2 border-primary/20 text-xs font-black uppercase tracking-widest gap-2 bg-background hover:bg-primary/5 shadow-sm active:scale-95 transition-all">
                                                    <Zap className="w-3.5 h-3.5 text-primary fill-primary/20" />
                                                    Smart Select
                                                </Button>
                                            </PopoverTrigger>
                                            <SmartSelectionPopover
                                                totalRecords={assignmentsPagination.totalRecords}
                                                currentPageCount={assignments.length}
                                                onSelectAll={() => {
                                                    setIsAllSelected(true);
                                                    setSelectedAssignmentIds([]); // Clear individual list when global is on
                                                    showSnackbar(`Selected all ${assignmentsPagination.totalRecords} contacts in sequence`, 'success');
                                                }}
                                                onSelectPage={() => {
                                                    setSelectedAssignmentIds(assignments.map(a => a.assignmentId));
                                                    setIsAllSelected(false);
                                                }}
                                                onApply={handleSmartSelection}
                                            />
                                        </Popover>

                                        {isAllSelected && (
                                            <Badge className="h-10 px-4 rounded-xl bg-primary text-white text-[10px] font-black uppercase tracking-widest animate-in zoom-in-90 duration-300">
                                                GLOBAL SELECTION: {assignmentsPagination.totalRecords} CONTACTS
                                            </Badge>
                                        )}
                                    </div>
                                }
                                selectable={{
                                    selectedIds: selectedAssignmentIds,
                                    isAllSelected: isAllSelected,
                                    onSelectionChange: (ids) => {
                                        setSelectedAssignmentIds(ids);
                                        setIsAllSelected(false);
                                    }
                                }}
                                bulkActions={[
                                    { label: 'Pause', onClick: () => handleBulkAction('PAUSE'), icon: Pause },
                                    { label: 'Resume', onClick: () => handleBulkAction('RESUME'), icon: Play },
                                    { label: 'Restart', onClick: () => handleBulkAction('RESTART'), icon: RotateCcw, variant: 'secondary' },
                                    { label: 'Retry Failed', onClick: () => handleBulkAction('RETRY_FAILED'), icon: RefreshCw },
                                    { label: 'Remove', onClick: () => handleBulkAction('REMOVE'), icon: Trash2, variant: 'secondary' }
                                ]}
                                data={assignments}
                                loading={isBulkActionLoading}
                                columns={[
                                    {
                                        header: 'Contact',
                                        accessor: 'email',
                                        render: (a) => (
                                            <div className="flex flex-col">
                                                <span className="font-bold text-sm text-foreground">{a.displayName || a.email.split('@')[0]}</span>
                                                <div className="flex items-center gap-1.5 opacity-60">
                                                    <span className="text-[10px] font-medium">{a.email}</span>
                                                    {a.companyName && a.companyName !== 'Unknown' && (
                                                        <>
                                                            <span className="text-[10px]">•</span>
                                                            <span className="text-[10px] text-primary font-bold">{a.companyName}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    },
                                    {
                                        header: 'Status',
                                        accessor: 'status',
                                        render: (a) => (
                                            <div className="flex flex-col gap-1">
                                                <StatusBadge
                                                    variant={
                                                        a.status === 'ACTIVE' ? 'success' :
                                                            a.status === 'REPLIED' ? 'indigo' :
                                                                a.status === 'BOUNCED' ? 'destructive' :
                                                                    a.status === 'FAILED' ? 'warning' : 'default'
                                                    }
                                                    dot={a.status === 'ACTIVE'}
                                                >
                                                    {a.status}
                                                </StatusBadge>
                                                {a.pauseReason && (
                                                    <span className="text-[9px] text-muted-foreground italic truncate max-w-[120px]">{a.pauseReason}</span>
                                                )}
                                            </div>
                                        )
                                    },
                                    {
                                        header: t('sequences.table.email'),
                                        accessor: 'email',
                                        render: (a) => (
                                            <span className="text-[11px] font-mono text-foreground">{a.email}</span>
                                        )
                                    },
                                    {
                                        header: t('sequences.table.company'),
                                        accessor: 'companyName',
                                        render: (a) => (
                                            <span className="text-[11px] font-medium text-foreground">
                                                {a.companyName || <span className="text-muted-foreground italic">No company</span>}
                                            </span>
                                        )
                                    },
                                    {
                                        header: t('sequences.table.historyCount'),
                                        accessor: 'historyCount',
                                        render: (a) => (
                                            <span className="text-[11px] font-bold text-center block">
                                                {a.historyCount || 0}
                                            </span>
                                        )
                                    },
                                    {
                                        header: t('sequences.table.nextScheduled'),
                                        accessor: 'nextStepScheduledAt',
                                        render: (a) => (
                                            <span className="text-[10px] text-foreground">
                                                {a.nextStepScheduledAt ? formatDateTime(a.nextStepScheduledAt, {}, i18n.language) : '-'}
                                            </span>
                                        )
                                    },
                                    {
                                        header: t('sequences.table.triggerNextAction'),
                                        accessor: 'currentStepNumber',
                                        render: (a) => {
                                            const stageText = a.currentStepLabel ? a.currentStepLabel : t('sequences.linear.stepNumberBadge', { number: a.currentStepNumber });
                                            return (
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-2 font-mono text-xs font-bold">
                                                        <span className="bg-muted px-1.5 py-0.5 rounded border uppercase tracking-wider">{stageText}</span>
                                                    </div>
                                                    <span className="text-[10px] text-foreground font-semibold">{getAssignmentTriggerLabel(a, t)}</span>
                                                    <span className="text-[10px] text-muted-foreground opacity-80 font-medium">{getAssignmentNextAction(a, t, i18n.resolvedLanguage)}</span>
                                                </div>
                                            )
                                        }
                                    },
                                    {
                                        header: t('sequences.table.source'),
                                        accessor: 'enrollmentSource',
                                        render: (a) => (
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] font-black uppercase tracking-widest text-primary">{getEnrollmentSourceLabel(a.enrollmentSource, t)}</span>
                                                <span className="text-[10px] text-muted-foreground font-medium">
                                                    {t('sequences.table.enteredOn', { date: formatDate(a.createdAt, { dateStyle: 'medium' }, i18n.resolvedLanguage) })}
                                                </span>
                                            </div>
                                        )
                                    },
                                    {
                                        header: t('sequences.table.latestActivity'),
                                        accessor: 'lastActivityAt',
                                        render: (a) => (
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] font-semibold text-foreground">
                                                    {a.latestActionType ? a.latestActionType.replace(/_/g, ' ') : t('sequences.table.noTraceYet')}
                                                </span>
                                                <span className="text-[10px] text-muted-foreground font-medium">
                                                    {a.lastActivityAt ? formatDateTime(a.lastActivityAt, {}, i18n.resolvedLanguage) : t('sequences.table.noActivityRecorded')}
                                                </span>
                                            </div>
                                        )
                                    },
                                    {
                                        header: '',
                                        accessor: 'actions',
                                        className: 'text-right',
                                        render: (a) => (
                                            <div className="flex items-center justify-end gap-1 px-4">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 rounded-lg"
                                                    onClick={() => fetchAssignmentHistory(a.assignmentId)}
                                                    title={t('sequences.actions.visualizationHistory')}
                                                >
                                                    <Activity className="w-3.5 h-3.5 text-muted-foreground" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 rounded-lg text-primary"
                                                    onClick={() => handleAssignmentRestart(a.assignmentId)}
                                                    title={t('sequences.actions.restart')}
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5" />
                                                </Button>

                                                {a.status === 'FAILED' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 rounded-lg text-emerald-600 hover:bg-emerald-50"
                                                        onClick={() => retryAssignment(a.assignmentId)}
                                                        title={t('sequences.actions.retryFailed')}
                                                    >
                                                        <RefreshCw className="w-3.5 h-3.5" />
                                                    </Button>
                                                )}

                                                {a.status === 'PAUSED' ? (
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-emerald-600" onClick={() => handleAssignmentStatusToggle(a.assignmentId, 'ACTIVE')}>
                                                        <Play className="w-3.5 h-3.5" />
                                                    </Button>
                                                ) : (a.status === 'ACTIVE' || a.status === 'PENDING') && (
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-amber-600" onClick={() => handleAssignmentStatusToggle(a.assignmentId, 'PAUSED')}>
                                                        <Pause className="w-3.5 h-3.5" />
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-destructive" onClick={() => handleAssignmentDelete(a.assignmentId)}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        )
                                    }
                                ]}
                                filter={{
                                    value: statusFilter,
                                    onChange: (val) => {
                                        setStatusFilter(val);
                                        fetchAssignments(selectedSequence._id, 1, assignmentsPagination.limit, val);
                                    },
                                    options: [
                                        { label: t('sequences.filters.allStatuses'), value: 'ALL' },
                                        { label: t('status.active'), value: 'ACTIVE' },
                                        { label: t('sequences.table.replied'), value: 'REPLIED' },
                                        { label: t('status.failed'), value: 'FAILED' },
                                        { label: t('status.paused'), value: 'PAUSED' },
                                        { label: t('sequences.filters.bounced'), value: 'BOUNCED' }
                                    ]
                                }}
                                emptyState={
                                    <div className="py-20 flex flex-col items-center opacity-40">
                                        <Users className="w-12 h-12 mb-3" />
                                        <p className="text-xs font-black uppercase tracking-widest">{t('sequences.empty.waitingForEnrollments')}</p>
                                    </div>
                                }
                                pagination={assignmentsPagination}
                                onPageChange={(p) => fetchAssignments(selectedSequence._id, p)}
                                onLimitChange={(l) => {
                                    setAssignmentsPagination(prev => ({ ...prev, limit: l, page: 1 }));
                                    fetchAssignments(selectedSequence._id, 1, l);
                                }}
                            />
                        </CardContent>
                    </Card>
                </div>
            </PageShell>
        );
    };

    const _renderEditMode = () => {
        if (selectedSequence.isGraph) {
            return (
                <div className="w-full relative z-40 bg-background flex flex-col pt-4 overflow-hidden min-h-[85vh]">
                    <div className="px-6 mb-4 flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-bold flex items-center gap-2"><Workflow className="w-6 h-6 text-primary" />{t('sequences.editor.flowOrchestration', { name: selectedSequence.name })}</h2>
                            <p className="text-sm text-muted-foreground">{t('sequences.editor.flowDescription')}</p>
                        </div>
                    </div>
                    <SequenceGraphEditor
                        sequence={selectedSequence}
                        onSave={async (nodes, edges) => {
                            try {
                                await api.put(`/api/sequences/${selectedSequence._id}/graph`, { nodes, edges, publish: true });

                                // Auto-activate sequence if it was a DRAFT
                                if (selectedSequence.status === 'DRAFT') {
                                    await api.put(`/api/sequences/${selectedSequence._id}/status`, { status: 'ACTIVE' });
                                }

                                showSnackbar(t('sequences.messages.graphPublished'), 'success');
                                fetchSequenceDetail(selectedSequence._id);
                                navigate(`/sequences/${selectedSequence._id}`);
                            } catch (err) {
                                notifyError(err, 'sequences.messages.graphPublishFailed');
                            }
                        }}
                        onAutoSave={async (nodes, edges) => {
                            try {
                                await api.put(`/api/sequences/${selectedSequence._id}/graph`, { nodes, edges, publish: false });
                                // Don't close editor or show big snackbar for background auto-save
                                console.log("Graph saved in background");
                            } catch (err) {
                                console.error("Auto-save failed", err);
                            }
                        }}
                        onCancel={() => navigate(`/sequences/${selectedSequence._id}`)}
                    />
                </div>
            )
        }

        return (
            <>
                <div className="space-y-6">
                    <div className="flex items-center gap-4 mb-4 text-sm mt-2">
                        <button onClick={() => navigate(`/sequences/${selectedSequence._id}`)} className="text-muted-foreground hover:text-foreground">← {t('sequences.actions.cancelEdit')}</button>
                    </div>

                    <div className="flex justify-between items-center bg-card p-6 border rounded-xl shadow-sm">
                        <div>
                            <h2 className="text-2xl font-bold">{t('sequences.editor.editBlueprint')}</h2>
                            <p className="text-sm text-muted-foreground">{t('sequences.editor.defineTimeline', { name: selectedSequence.name })}</p>
                        </div>
                        <button onClick={saveEditingSteps} className="bg-primary text-primary-foreground px-6 py-2 rounded-md font-medium hover:bg-primary/90">
                            {t('sequences.actions.saveVariablesFlow')}
                        </button>
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 p-4 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm">
                        <strong>{t('sequences.editor.supportedVariables')}:</strong> {'{{firstName}}, {{lastName}}, {{company}}, {{title}}, {{email}}'}
                    </div>

                    <div className="space-y-6">
                        {editingSteps.map((step, idx) => {
                            const preview = stepPreviews[idx];
                            const subjectPreview = preview?.resolvedSubject || step.subjectTemplate || t('sequences.editor.subjectPlaceholder');
                            const bodyPreview = preview?.resolvedBodyHtml || step.bodyTemplate || `<p class="opacity-50">${t('sequences.editor.bodyPlaceholder')}</p>`;

                            return (
                                <div key={idx} className="border bg-card rounded-[2rem] p-6 shadow-sm">
                                    <div className="flex items-start justify-between gap-4 mb-6">
                                        <div className="flex items-center gap-3">
                                            <span className="flex items-center justify-center w-10 h-10 bg-primary text-primary-foreground rounded-full font-bold">{step.stepNumber}</span>
                                            <div>
                                                <h4 className="font-semibold text-lg">{t('sequences.linear.stepTitle', { number: step.stepNumber })}</h4>
                                                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                                    {t('sequences.editor.wait')} {step.delayDays || 0} {t('sequences.editor.daysBeforeExecuting')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => openStepEditor(idx)}>
                                                <Edit className="w-3.5 h-3.5 mr-2" /> {t('actions.edit', { defaultValue: 'Edit' })}
                                            </Button>
                                            <Button variant="ghost" size="sm" className="rounded-xl text-red-500 hover:text-red-700" onClick={() => removeStep(idx)}>
                                                <Trash2 className="w-3.5 h-3.5 mr-2" /> {t('actions.delete')}
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="bg-white text-[#1e1e1f] rounded-[2rem] shadow-xl border border-zinc-200/50 overflow-hidden min-h-[280px] flex flex-col">
                                        <div className="border-b border-zinc-100 p-6 bg-zinc-50/70">
                                            <h3 className="text-xl font-bold tracking-tight text-zinc-900">{subjectPreview}</h3>
                                        </div>
                                        <div className="p-8 flex-1 bg-white">
                                            <div className="prose prose-zinc prose-sm md:prose-base max-w-none text-zinc-800" dangerouslySetInnerHTML={{ __html: bodyPreview }} />
                                        </div>
                                        {preview?.attachments?.length > 0 && (
                                            <div className="bg-zinc-50 border-t border-zinc-100 p-6 flex flex-col gap-2">
                                                <h4 className="text-xs font-black uppercase tracking-widest text-zinc-400">Attachments ({preview.attachments.length})</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {preview.attachments.map((att, previewIdx) => (
                                                        <div key={`${att.filename || 'attachment'}-${previewIdx}`} className="flex items-center gap-2 bg-white border border-zinc-200 px-3 py-2 rounded-xl text-sm shadow-sm max-w-[220px]">
                                                            <FileText className="w-4 h-4 text-zinc-400 shrink-0" />
                                                            <span className="truncate text-zinc-700 font-medium">{att.filename}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        <button
                            onClick={addStep}
                            className="w-full border-2 border-dashed py-8 px-4 rounded-xl flex flex-col items-center justify-center gap-2 text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-all"
                        >
                            <Plus className="w-6 h-6" />
                            <span className="font-medium">{t('sequences.actions.addNextStep')}</span>
                        </button>
                    </div>
                </div>

                <Sheet open={isStepEditorOpen} onOpenChange={(open) => { if (!open) requestCloseStepEditor(); }}>
                    <SheetContent side="right" className="w-full max-w-full sm:max-w-[1200px] p-0 border-l shadow-2xl overflow-hidden">
                        {activeStepDraft && (
                            <TemplateStyleEmailEditorShell
                                className="h-full"
                                title={t('sequences.editor.editBlueprint')}
                                subtitle={t('sequences.linear.stepTitle', { number: activeStepDraft.stepNumber })}
                                leadIcon={Mail}
                                metaSection={
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Step Identity</label>
                                            <input
                                                type="text"
                                                value={t('sequences.linear.stepTitle', { number: activeStepDraft.stepNumber })}
                                                readOnly
                                                className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl py-3.5 px-5 transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('sequences.editor.scheduleWait')}</label>
                                            <input
                                                type="number"
                                                value={activeStepDraft.delayDays || 0}
                                                min="0"
                                                onChange={(e) => setActiveStepDraft((prev) => ({ ...(prev || {}), delayDays: parseInt(e.target.value, 10) || 0 }))}
                                                className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl py-3.5 px-5 transition-all"
                                            />
                                        </div>
                                    </div>
                                }
                                subject={activeStepDraft.subjectTemplate || ''}
                                onSubjectChange={(value) => setActiveStepDraft((prev) => ({ ...(prev || {}), subjectTemplate: value }))}
                                subjectPlaceholder={t('sequences.editor.subjectPlaceholder')}
                                variableNames={SEQUENCE_EDITOR_VARIABLES}
                                onInsertVariable={(variable) => insertStepVariable('bodyTemplate', variable)}
                                bodyHtml={activeStepDraft.bodyTemplate || ''}
                                onBodyChange={(value) => setActiveStepDraft((prev) => ({ ...(prev || {}), bodyTemplate: value }))}
                                onImageUpload={handleStepImageUpload}
                                isUploading={isUploadingStepImage}
                                attachments={activeStepDraft.attachments || []}
                                onAttachmentUpload={handleStepAttachmentUpload}
                                onAttachmentRemove={removeStepAttachment}
                                previewData={activeStepPreview}
                                previewLoading={isActiveStepPreviewLoading}
                                onDiscard={requestCloseStepEditor}
                                onSave={saveActiveStepDraft}
                                discardLabel={t('actions.cancel')}
                                saveLabel={t('actions.save')}
                                saveIcon={FileText}
                            />
                        )}
                    </SheetContent>
                </Sheet>
            </>
        );
    };

    const _renderCreate = () => {
        return (
            <PageShell
                title="New Sequence"
                subtitle="Architecture Blueprint"
                icon={Workflow}
                actions={
                    <Button variant="ghost" onClick={() => navigate('/sequences')} className="rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground font-black text-[10px] uppercase tracking-widest px-4">
                        <ChevronLeft className="h-3 w-3 mr-1.5" /> Back to Blueprints
                    </Button>
                }
            >
                <div className="max-w-4xl mx-auto py-8">
                    <Card className="rounded-[2.5rem] border-2 border-border/50 shadow-2xl overflow-hidden">
                        <CardContent className="p-8">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-12 h-12 rounded-2xl bg-primary text-white flex items-center justify-center rotate-3 shadow-lg">
                                    <Workflow className="w-6 h-6" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-black tracking-tight leading-tight">Create New Sequence</h2>
                                    <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest opacity-70">Architecture Blueprint</p>
                                </div>
                            </div>

                            <div className="space-y-8">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Sequence Name</label>
                                    <Input
                                        value={newSeqName}
                                        onChange={(e) => setNewSeqName(e.target.value)}
                                        placeholder="e.g. Q4 Inbound Follow-up"
                                        className="h-14 rounded-2xl bg-muted/30 border-none focus-visible:ring-primary/20 text-lg font-bold placeholder:font-medium placeholder:opacity-30"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Architecture Format</label>
                                        <div className="grid grid-cols-2 gap-3 p-1.5 bg-muted/40 rounded-2xl">
                                            <button
                                                onClick={() => setNewSeqFormat('GRAPH')}
                                                className={cn(
                                                    "flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all duration-300",
                                                    newSeqFormat === 'GRAPH' ? "bg-card text-foreground shadow-sm ring-1 ring-border/50" : "text-muted-foreground hover:bg-card/50"
                                                )}
                                            >
                                                <Zap className={cn("w-4 h-4", newSeqFormat === 'GRAPH' ? "text-primary" : "opacity-40")} />
                                                <span className="text-[10px] font-black uppercase tracking-widest">Graph</span>
                                            </button>
                                            <button
                                                onClick={() => setNewSeqFormat('LINEAR')}
                                                className={cn(
                                                    "flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all duration-300",
                                                    newSeqFormat === 'LINEAR' ? "bg-card text-foreground shadow-sm ring-1 ring-border/50" : "text-muted-foreground hover:bg-card/50"
                                                )}
                                            >
                                                <ListTree className={cn("w-4 h-4", newSeqFormat === 'LINEAR' ? "text-primary" : "opacity-40")} />
                                                <span className="text-[10px] font-black uppercase tracking-widest">Linear</span>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Sending Source</label>
                                        <div className="grid grid-cols-2 gap-3 p-1.5 bg-muted/40 rounded-2xl">
                                            <button
                                                onClick={() => setNewSeqSourceType('ACCOUNT')}
                                                className={cn(
                                                    "flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all duration-300",
                                                    newSeqSourceType === 'ACCOUNT' ? "bg-card text-foreground shadow-sm ring-1 ring-border/50" : "text-muted-foreground hover:bg-card/50"
                                                )}
                                            >
                                                <Users className={cn("w-4 h-4", newSeqSourceType === 'ACCOUNT' ? "text-primary" : "opacity-40")} />
                                                <span className="text-[10px] font-black uppercase tracking-widest">Account</span>
                                            </button>
                                            <button
                                                onClick={() => setNewSeqSourceType('POOL')}
                                                className={cn(
                                                    "flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all duration-300",
                                                    newSeqSourceType === 'POOL' ? "bg-card text-foreground shadow-sm ring-1 ring-border/50" : "text-muted-foreground hover:bg-card/50"
                                                )}
                                            >
                                                <ShieldCheck className={cn("w-4 h-4", newSeqSourceType === 'POOL' ? "text-primary" : "opacity-40")} />
                                                <span className="text-[10px] font-black uppercase tracking-widest">Pool</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">
                                        {newSeqSourceType === 'ACCOUNT' ? 'Default Sender Account' : 'Sender Pool'}
                                    </label>
                                    <Select
                                        value={newSeqSourceType === 'ACCOUNT' ? newSeqSenderAccountId : newSeqPoolId}
                                        onValueChange={newSeqSourceType === 'ACCOUNT' ? setNewSeqSenderAccountId : setNewSeqPoolId}
                                    >
                                        <SelectTrigger className="h-14 rounded-2xl bg-muted/30 border-none focus:ring-primary/20 font-bold">
                                            <SelectValue placeholder={`Select ${newSeqSourceType === 'ACCOUNT' ? 'Account' : 'Pool'}`} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {newSeqSourceType === 'ACCOUNT' ? (
                                                mailAccounts.map(acc => (
                                                    <SelectItem key={acc._id} value={acc._id} className="font-medium">{acc.user}</SelectItem>
                                                ))
                                            ) : (
                                                senderPools.map(pool => (
                                                    <SelectItem key={pool._id} value={pool._id} className="font-medium">{pool.name}</SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="pt-4 border-t border-border/40">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <Clock className="w-5 h-5 text-primary" />
                                            <div>
                                                <h3 className="text-sm font-bold">Smart Scheduling</h3>
                                                <p className="text-[10px] text-muted-foreground font-medium">Define when outreach is performed.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 rounded-xl">
                                            <Checkbox
                                                id="schedule-toggle-create"
                                                checked={startMode === 'SCHEDULED'}
                                                onCheckedChange={(checked) => setStartMode(checked ? 'SCHEDULED' : 'IMMEDIATE')}
                                                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                            />
                                            <label htmlFor="schedule-toggle-create" className="text-[10px] font-black uppercase tracking-widest cursor-pointer select-none">
                                                Active Schedule
                                            </label>
                                        </div>
                                    </div>

                                    {startMode === 'SCHEDULED' && (
                                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Activation Date</label>
                                                <Input
                                                    type="datetime-local"
                                                    value={scheduledAt}
                                                    onChange={(e) => setScheduledAt(e.target.value)}
                                                    className="h-12 rounded-2xl bg-muted/30 border-none focus-visible:ring-primary/20 font-bold"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-6 mt-6">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Default Delivery Timezone (Fallback)</label>
                                            <p className="text-[9px] text-muted-foreground -mt-1 mb-1">Used if contact's local timezone cannot be resolved from their location.</p>
                                            <TimezoneSelector value={timezone} onChange={setTimezone} />
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Working Days</label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                                                    <button
                                                        key={day}
                                                        onClick={() => {
                                                            if (workingDays.includes(idx)) setWorkingDays(workingDays.filter(d => d !== idx));
                                                            else setWorkingDays([...workingDays, idx].sort());
                                                        }}
                                                        className={cn(
                                                            "h-10 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300",
                                                            workingDays.includes(idx) ? "bg-primary text-white shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"
                                                        )}
                                                    >
                                                        {day}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Window Start</label>
                                                <Input
                                                    type="time"
                                                    value={sendingWindowStart}
                                                    onChange={(e) => setSendingWindowStart(e.target.value)}
                                                    className="h-12 rounded-2xl bg-muted/30 border-none focus-visible:ring-primary/20 font-bold"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Window End</label>
                                                <Input
                                                    type="time"
                                                    value={sendingWindowEnd}
                                                    onChange={(e) => setSendingWindowEnd(e.target.value)}
                                                    className="h-12 rounded-2xl bg-muted/30 border-none focus-visible:ring-primary/20 font-bold"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border/40">
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Start Mode</label>
                                                <Select value={startMode} onValueChange={setStartMode}>
                                                    <SelectTrigger className="h-12 rounded-2xl bg-muted/30 border-none focus-visible:ring-primary/20 font-bold">
                                                        <SelectValue placeholder="Select start mode" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                                                        <SelectItem value="IMMEDIATE" className="font-bold cursor-pointer hover:bg-muted focus:bg-indigo-500/10 transition-colors py-3 text-indigo-900">Immediate</SelectItem>
                                                        <SelectItem value="SCHEDULED" className="font-bold cursor-pointer hover:bg-muted focus:bg-indigo-500/10 transition-colors py-3 text-indigo-900">Scheduled</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">End At (Optional)</label>
                                                <Input
                                                    type="datetime-local"
                                                    value={endAt}
                                                    onChange={(e) => setEndAt(e.target.value)}
                                                    className="h-12 rounded-2xl bg-muted/30 border-none focus-visible:ring-primary/20 font-bold"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border/40">
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Min Delay (sec)</label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={minDelaySec}
                                                    onChange={(e) => setMinDelaySec(parseInt(e.target.value) || 0)}
                                                    className="h-12 rounded-2xl bg-muted/30 border-none focus-visible:ring-primary/20 font-bold"
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Max Delay (sec)</label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={maxDelaySec}
                                                    onChange={(e) => setMaxDelaySec(parseInt(e.target.value) || 0)}
                                                    className="h-12 rounded-2xl bg-muted/30 border-none focus-visible:ring-primary/20 font-bold"
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Per Day Limit</label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={perDayLimit}
                                                    onChange={(e) => setPerDayLimit(parseInt(e.target.value) || 0)}
                                                    className="h-12 rounded-2xl bg-muted/30 border-none focus-visible:ring-primary/20 font-bold"
                                                    placeholder="0 (unlimited)"
                                                />
                                                <p className="text-[9px] text-muted-foreground">0 means unlimited</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-12 flex gap-4">
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate('/sequences')}
                                    className="flex-1 h-14 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-muted"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleCreateSequence}
                                    disabled={!newSeqName || (startMode === 'SCHEDULED' && !scheduledAt)}
                                    className="flex-[2] h-14 rounded-2xl bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 text-[11px] font-black uppercase tracking-widest border-none"
                                >
                                    Create Blueprint
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </PageShell>
        );
    };

    return (
        <>
            <div className="relative">
                {viewMode === 'LIST' && _renderList()}
                {viewMode === 'CREATE' && _renderCreate()}
                {viewMode === 'DETAIL' && selectedSequence && _renderDetail()}
                {viewMode === 'EDIT_STEPS' && selectedSequence && _renderEditMode()}

                <Sheet open={isHistoryDrawerOpen} onOpenChange={setIsHistoryDrawerOpen}>
                    <SheetContent className="sm:max-w-md border-l shadow-2xl overflow-y-auto hide-scrollbar">
                        <SheetHeader className="pb-6 border-b">
                            <SheetTitle className="text-xl font-black flex items-center gap-2">
                                <Activity className="w-6 h-6 text-primary" /> {t('sequences.history.executionTrace')}
                            </SheetTitle>
                            <SheetDescription className="text-xs font-bold uppercase tracking-widest opacity-60">
                                {activeHistory?.contact?.firstName} {activeHistory?.contact?.lastName} ({activeHistory?.contact?.email})
                            </SheetDescription>
                        </SheetHeader>

                        <div className="mt-8 relative">
                            {/* Vertical Line */}
                            <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-border/60" />

                            <div className="space-y-10 pl-4">
                                {activeHistory?.history?.slice().reverse().map((event, idx) => (
                                    <div key={idx} className="relative pl-10">
                                        {/* Dot */}
                                        <div className={cn(
                                            "absolute left-[-2px] top-1 w-9 h-9 rounded-full border-4 border-background flex items-center justify-center shadow-sm z-10",
                                            event.status === 'SUCCESS' ? 'bg-emerald-500 text-white' :
                                                event.status === 'STOPPED' ? 'bg-amber-500 text-white' :
                                                    event.status === 'FAILED' ? 'bg-red-500 text-white' : 'bg-muted text-muted-foreground'
                                        )}>
                                            {event.actionType === 'EMAIL_SENT' ? <Mail className="w-4 h-4" /> :
                                                event.actionType === 'GRAPH_TRANSITION' ? <Workflow className="w-4 h-4" /> :
                                                    <Activity className="w-4 h-4" />}
                                        </div>

                                        <div className="space-y-1">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-xs font-black uppercase tracking-widest text-foreground">{event.actionType.replace(/_/g, ' ')}</h4>
                                                <span className="text-[10px] font-bold text-muted-foreground opacity-60">{formatDateTime(event.executedAt, {}, i18n.resolvedLanguage)}</span>
                                            </div>
                                            <div className="text-[11px] font-medium text-muted-foreground leading-relaxed">
                                                {event.actionType === 'ENROLLED' ? t('sequences.history.enrolled') :
                                                    event.actionType === 'GRAPH_TRANSITION' ? t('sequences.history.transitionedTo', { target: event.details?.targetType || t('sequences.history.nextNode') }) :
                                                        event.actionType === 'GRAPH_STEP_COMPLETED' && event.details?.stepLabel ? t('sequences.history.sentStep', { step: event.details.stepLabel.replace(/_/g, ' ') }) :
                                                            event.details?.reason || event.details?.subject || t('sequences.history.stepCompleted', { number: event.stepNumber, status: event.status })}
                                            </div>
                                            {event.details?.errorMessage && (
                                                <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded-lg text-[10px] font-bold text-red-600 animate-pulse">
                                                    {t('status.failed')}: {event.details.errorMessage}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {(!activeHistory?.history || activeHistory.history.length === 0) && (
                                    <div className="py-10 text-center text-[10px] font-black uppercase tracking-widest opacity-40">
                                        {t('sequences.history.initializing')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>

                <Sheet open={isPoolBuilderOpen} onOpenChange={setIsPoolBuilderOpen}>
                    <SheetContent side="right" className="max-w-3xl rounded-[2rem] border-2 border-border p-0 overflow-hidden">
                        <SheetHeader className="px-6 py-5 border-b border-border bg-muted/10">
                            <SheetTitle className="text-xl font-black tracking-tight">Create Sender Pool</SheetTitle>
                            <SheetDescription>
                                Build a multi-account sender pool here and attach it directly to this sequence flow.
                            </SheetDescription>
                        </SheetHeader>

                        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
                            <div>
                                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Pool Name</label>
                                <input
                                    type="text"
                                    value={newPoolName}
                                    onChange={(e) => setNewPoolName(e.target.value)}
                                    placeholder="e.g. Founder Outreach Pool"
                                    className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Rotation Strategy</label>
                                <Select value={newPoolRotationStrategy} onValueChange={setNewPoolRotationStrategy}>
                                    <SelectTrigger className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold h-12 px-5 transition-all">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                                        <SelectItem value="ROUND_ROBIN" className="font-bold cursor-pointer hover:bg-muted focus:bg-primary/10 transition-colors py-3">
                                            Round Robin (Sequential)
                                        </SelectItem>
                                        <SelectItem value="FIRST_AVAILABLE" className="font-bold cursor-pointer hover:bg-muted focus:bg-primary/10 transition-colors py-3">
                                            First Available (Priority)
                                        </SelectItem>
                                        <SelectItem value="LEAST_USED_TODAY" className="font-bold cursor-pointer hover:bg-muted focus:bg-primary/10 transition-colors py-3">
                                            Least Used (Load Balanced)
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Assign Accounts</label>
                                <div className="max-h-80 overflow-y-auto border-2 border-border rounded-2xl p-4 space-y-3 bg-muted/5 shadow-inner text-foreground">
                                    {mailAccounts.length === 0 ? (
                                        <div className="py-8 text-center text-sm font-medium text-muted-foreground italic">No mail accounts connected.</div>
                                    ) : (
                                        mailAccounts.map(acc => {
                                            const isSelected = newPoolAccountIds.includes(acc._id);
                                            return (
                                                <button
                                                    type="button"
                                                    key={acc._id}
                                                    onClick={() => togglePoolAccount(acc._id)}
                                                    className={`w-full flex items-center p-4 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border/50 bg-background hover:border-primary/40'}`}
                                                >
                                                    <div className={`w-5 h-5 rounded flex items-center justify-center border-2 mr-3 ${isSelected ? 'bg-primary border-primary transition-all scale-110' : 'border-muted-foreground/30'}`}>
                                                        {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-xs truncate">{acc.from || acc.user}</span>
                                                            <span className="text-[10px] font-black uppercase tracking-tighter opacity-50 px-1 rounded bg-muted border border-border">{acc.providerType}</span>
                                                        </div>
                                                        <div className="text-[10px] font-semibold text-muted-foreground truncate">{acc.user}</div>
                                                    </div>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>

                        <SheetFooter className="px-6 py-5 border-t border-border bg-muted/10">
                            <Button variant="ghost" onClick={() => setIsPoolBuilderOpen(false)} className="h-11 rounded-xl border border-border">
                                Cancel
                            </Button>
                            <Button onClick={handleCreateSenderPoolInline} disabled={isSavingPool} className="h-11 rounded-xl font-bold">
                                {isSavingPool ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                                Create Pool
                            </Button>
                        </SheetFooter>
                    </SheetContent>
                </Sheet>

            </div>
            {isEnrollModalOpen && selectedSequence && (
                <SequenceEnrollModal
                    preSelectedSequenceId={selectedSequence._id}
                    onClose={() => setIsEnrollModalOpen(false)}
                    onComplete={() => {
                        setIsEnrollModalOpen(false);
                        fetchAssignments(selectedSequence._id);
                        fetchSequenceDetail(selectedSequence._id);
                    }}
                />
            )}

            <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
                        <AlertDialogDescription>{confirmDialog.message}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{confirmDialog.cancelText}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDialog.onConfirm}
                            className={confirmDialog.variant === 'destructive' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
                        >
                            {confirmDialog.confirmText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
